// #region Imports
import WorkVersionChip from "work/work-agreements/versions/_views/work-version-chip";
import ProposalPageContent from "work/proposals/_views/_shared/proposal-page-content";
import WorkVersion from "work/work-agreements/versions/work-version";
import PageTitleBreadcrumb, { breadcrumbHeadingName } from "_shared/_views/breadcrumbs/page-title-breadcrumb";
import BlockIcon from "@mui/icons-material/Block";
import useSessionPermissionsLoadState from "permissions/session/use-session-permissions-load-state";
import CloseIcon from "@mui/icons-material/Close";
import CommentIcon from "@mui/icons-material/Comment";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import GroupsIcon from "@mui/icons-material/Groups";
import SendIcon from "@mui/icons-material/Send";
import UndoIcon from "@mui/icons-material/Undo";
import WorkIcon from "@mui/icons-material/Work";
import Badge from "@mui/material/Badge";
import { ThemeProvider } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { skipToken } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import type { JsonPatchOperation } from "_shared/_api/attorney-hub-api-service";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import {
  TraverserStopScopeContext,
  useTraverserStopScope,
} from "_shared/_views/highlights/traverser-stop-scope";
import Loader from "_shared/_views/loading/loader";
import EntityMember from "_shared/entity-members/entity-member";
import EditFeeScheduleCategoryDrawer from "_shared/fee-schedules/categories/_views/templates/edit-fee-schedule-category-drawer";
import ExistingFeeScheduleCategoryDrawer from "_shared/fee-schedules/categories/_views/templates/existing-fee-schedule-category-drawer";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import { getFeeScheduleTeamMembers, getMarketplaceTeamId } from "_shared/fee-schedules/categories/fee-schedule-team-members";
import { staffingForRates } from "_shared/fee-schedules/categories/fee-schedule-template-staffing";
import useReconcileFeeScheduleStaffing from "_shared/fee-schedules/categories/use-reconcile-fee-schedule-staffing";
import {
  getFeeScheduleTeamRosterFromRedline,
  withStaffingSeatedOnTeamRedline,
} from "work/proposals/redlining/fee-schedule-redline/fee-schedule-staffing-redline";
import Guid from "_shared/guids/guid";
import { chain, uniqBy } from "lodash";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { reportProposalMissingItemSaveFailure } from "work/proposals/_views/_shared/missing-item-save-failure-snackbar";
import { showActionableErrorSnackbar, showErrorSnackbar } from "_shared/_errors/error-snackbar";
import { showProposalCommandError } from "work/proposals/_views/_shared/proposal-command-error";
import { HIDDEN_RATES_ADD_MESSAGE } from "work/proposals/_views/_shared/fee-schedule-hidden-rates";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { ConversationPane } from "messaging/conversations/conversation-links";
import useConversationPaneRequest from "messaging/conversations/use-conversation-pane-request";
import type Session from "users/__session/session";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { proposalListPath, ProposalStageChip } from "work/proposals/_views/proposals-page-tabs";
import { FeeScheduleCategoryMaskedCurrency, Masked, MaskedTeam } from "work/masked-fields";
import {
  ButtonContainer,
  Content,
  ProposalTab,
  TabContent,
  TabsContainer,
} from "work/_views/work-page-styled-components";
import "work/proposals/__extensions/users/user-extensions";
import { ProposalAction, ProposalStatus } from "work/proposals/__constants/proposal-lifecycle";
import {
  useApproveProposalMutation,
  useCancelProposalMutation,
  useDeleteProposalMutation,
  useGiveReviewerApprovalMutation,
  useHireProposalMutation,
  useQuitProposalTeamMutation,
  useRejectProposalMutation,
  useRequestProposalRevisionMutation,
  useSubmitProposalMutation,
} from "work/proposals/_api/proposal-api";
import { UpdateProposalAPIRequest } from "work/proposals/_api/commands/update-proposal-api-request";
import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import {
  clearLocalProposalById,
  getProposalIsNoLongerAvailable,
  isProposalLoadIssue,
  updateServerProposal,
  updateLocalProposal,
} from "work/proposals/_store/proposals-redux-slice";
import BulkChangeDecisionButton from "work/proposals/_views/_buttons/bulk-change-decision-button";
import ProposalSecondaryActions, {
  type ProposalSecondaryAction,
} from "work/proposals/_views/_buttons/proposal-secondary-actions";
import SubmitReviewButton from "work/proposals/_views/_buttons/submit-review-button";
import {
  getProposalOperationDropWarning,
  getProposalSaveSuccessMessage,
  getSaveConfirmationCopy,
  proposalSaveChangeLogHeading,
} from "work/proposals/_views/_shared/proposal-save-confirmation-copy";
import type { ConfirmationMode } from "work/proposals/_views/_shared/proposal-save-confirmation-copy";
import ProposalChangeSummaryList from "work/proposals/_views/_shared/proposal-change-summary-list";
import {
  buildProposalSaveSharePlan,
  proposalSaveAudienceLabel,
  proposalSubmitRecipientMember,
} from "work/proposals/_views/_shared/proposal-change-share-plan";
import { useProposalChangeDescriptionContext } from "work/proposals/_views/_shared/use-proposal-change-description-context";
import {
  CommentButton,
  CommentButtonLabel,
  HighlightCommentButton,
  IssueTrackerFooter,
  IssueTrackerHeader,
  ProposalActionButton,
  ProposalActions,
  ProposalLoaderContainer as LoaderContainer,
  ProposalPageActionButtons as ActionButtonsContainer,
  ProposalPageActionsContainer as PageActionsContainer,
  ProposalPageContainer as PageContainer,
  ProposalPageHeader as HeaderContainer,
  ProposalPageMain as Main,
  ProposalPageNotices as PageNotices,
  ProposalPageTitle as PageTitle,
  ProposalPageTitleContainer as TitleContainer,
  ProposalSidePanelColumn as SidePanelColumn,
  ProposalStickyPageHeader as StickyPageHeader,
  ProposalTabsBand,
} from "work/proposals/_views/_shared/proposal-styled-components";
import {
  getProposalDrawerWidth,
  NEW_FEE_CATEGORY_DRAWER_KEY,
  useProposalPageCanSeatSidePanel,
  useProposalPagePanels,
} from "work/proposals/_views/_shared/proposal-page-panels";
import { usePersistedProposalSaveOrchestration } from "work/proposals/_views/_shared/use-proposal-save-orchestration";
import useProposalTabPendingCounts from "work/proposals/_views/_shared/use-proposal-tab-pending-counts";
import useUnsavedProposalNavigationGuard from "work/proposals/_views/_shared/use-unsaved-proposal-navigation-guard";
import { navigationLeavesProposalEditor } from "work/proposals/_views/_shared/proposal-editor-paths";
import useAskBeforeUnload from "_shared/_views/use-ask-before-unload";
import useProposalPageAssistantScreenContext from "work/proposals/_views/_shared/use-proposal-page-assistant-screen-context";
import { paneFlushContent } from "work/_views/work-pane-layout";
import useWorkPaneTheme from "work/_views/work-pane-typography";
import useWorkPaneSize, {
  WorkPaneWidthContext,
} from "work/_views/use-work-pane-size";
import {
  isProposalAccessDeniedError,
  isProposalNoLongerAvailableError,
  PROPOSAL_ACCESS_DENIED_MESSAGE,
} from "work/proposals/_views/_shared/proposal-availability-error";
import ProposalLoadFailure from "work/proposals/_views/_shared/proposal-load-failure";
import {
  useApproveProposalNegotiationMutation,
  useCancelProposalNegotiationMutation,
  useCompleteProposalReviewMutation,
  useHireProposalNegotiationMutation,
  useMarkProposalChangesSeenMutation,
  useRejectProposalNegotiationMutation,
  useRequestProposalRevisionNegotiationMutation,
  useSubmitProposalCollaborationDraftMutation,
} from "work/proposals/negotiation/proposal-collaboration-api";
import {
  buildApproveProposalNegotiationCommand,
  buildCancelProposalNegotiationCommand,
  buildCompleteProposalReviewCommand,
  buildHireProposalNegotiationCommand,
  buildRejectProposalNegotiationCommand,
  buildRequestProposalRevisionNegotiationCommand,
  buildSubmitProposalDraftCommand,
} from "work/proposals/negotiation/proposal-command-builder";
import type { ProposalCollaborationEnvelope } from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  proposalEnvelopeReceived,
  proposalOperationsQueued,
} from "work/proposals/negotiation/proposal-collaboration-redux-slice";
import { proposalFieldRestoreLockReason } from "work/proposals/negotiation/proposal-field-restore";
import { proposalPublishableChanges } from "work/proposals/negotiation/proposal-publishable-changes";
import { buildProposalSubmissionRevertOperations } from "work/proposals/negotiation/proposal-submission-revert";
import useSubmitProposalDialog, {
  CANCELED_SUBMISSION,
} from "work/proposals/_views/_shared/use-submit-proposal-dialog";
import useProposalSubmitRecipient from "work/proposals/_views/_shared/use-proposal-submit-recipient";
import { isReviewerApprovalCurrent } from "work/proposals/negotiation/proposal-reviewer-approval-state";
import { proposalCommitmentConfirmationMessage } from "work/proposals/_views/_shared/proposal-pending-review-alert";
import { isProposalResolutionTargetResolvable } from "work/proposals/negotiation/proposal-resolution-target";
import { proposalRedlineBaselineJSON } from "work/proposals/negotiation/proposal-redline-baseline";
import { proposalFromCanonicalNegotiationJSON } from "work/proposals/negotiation/proposal-resolution-redline-adapter";
import { PageTab } from "work/proposals/_views/active/active-proposal-page-tabs";
import ConflictsTab from "work/proposals/_views/active/tabs/active-proposal-conflicts-tab";
import DetailsTab from "work/proposals/_views/active/tabs/active-proposal-details-tab";
import ClientTeamTab from "work/proposals/_views/active/tabs/active-proposal-client-team-tab";
import DiscountTab from "work/proposals/_views/active/tabs/active-proposal-discount-tab";
import FeeScheduleTab from "work/proposals/_views/active/tabs/active-proposal-fee-schedule-tab";
import PoliciesTab from "work/proposals/_views/active/tabs/active-proposal-policies-tab";
import TeamTab from "work/proposals/_views/active/tabs/active-proposal-team-tab";
import PublishedRatesOffer, { publishedRatesOfferTeamId } from "work/proposals/_views/drafts/published-rates-offer";
import {
  clearUnsavedCommentsByProposalId,
  getCommentsByProposal,
} from "work/proposals/comments/_store/comments-redux-slice";
import CommentsDrawer, { proposalCommentsTitle } from "work/proposals/comments/_view/comments-drawer";
import { oldestUnreadCommentId } from "work/proposals/comments/oldest-unread-comment";
import { getConversationUnreadCount } from "work/proposals/comments/threads/conversation-unread-count";
import { offersNegotiation } from "work/proposals/comments/threads/offers-negotiation";
import { useCommentPaneProposal } from "work/proposals/comments/comment-pane-proposal";
import {
  ProposalFieldHistoryRequestContext,
  ProposalFieldTraversalSource,
} from "work/proposals/negotiation/proposal-field-history-request-context";
import { ProposalCarriedOverNegotiationContext } from "work/proposals/negotiation/proposal-carried-over-negotiation-context";
import { useRevisedNegotiation } from "work/proposals/negotiation/revised-negotiation";
import { proposalActivityCoverage } from "work/proposals/negotiation/proposal-activity";
import ProposalActivityPopover, { proposalActivityTitle } from "work/proposals/negotiation/proposal-activity-popover";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import {
  getCommentThreadsByProposal,
  populateCommentThreadsByProposal,
} from "work/proposals/comments/threads/_store/comment-thread-redux-slice";
import IssueTraverser from "work/proposals/issues/_views/proposal-issue-traverser";
import { feeScheduleRowOrder } from "work/proposals/issues/proposal-traversal-stops";
import ProposalIssuesBadge from "work/proposals/issues/_views/proposal-issues-badge";
import { proposalFieldUnread, proposalUnreadChangeFields } from "work/proposals/negotiation/proposal-field-unread";
import {
  proposalTimelineItemPathForField,
  proposalTimelinePathForField,
} from "work/proposals/negotiation/proposal-field-timeline-path";
import { proposalTraversalKeepsCommentsPaneOpen } from "work/proposals/negotiation/proposal-traversal-comments-pane";
import { proposalFieldToMarkSeenOnTraversal } from "work/proposals/negotiation/proposal-field-traversal-seen";
import ProposalIssues, { type ProposalIssue } from "work/proposals/issues/proposal-issues";
import { revealFeeScheduleIssueCell } from "work/proposals/issues/fee-schedule-issue-cell";
import Proposal from "work/proposals/proposal";
import ProposalSaveSplitButton from "work/proposals/_views/_buttons/proposal-save-split-button";
import useSaveAsProposalTemplateAction from "work/proposal-templates/_hooks/use-save-as-proposal-template-action";
import useSessionUser from "permissions/session/use-session-user";
import { useWhoHasAccess } from "permissions/_views/records/who-has-access";
import RecordAccessButton from "work/_views/record-access-button";
import useProposalLeaderHandover from "work/proposals/leader-handover/_views/use-proposal-leader-handover";
import ProposalField, {
  ProposalFieldCategory,
  ProposalFieldCategoryType,
  ProposalFieldName,
} from "work/proposals/proposal-fields/proposal-field";
import {
  type ExistingCategoryMerge,
  withExistingCategoryMerges,
} from "work/proposals/redlining/fee-schedule-redline/existing-category-merges";
import { FeeScheduleRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-redline";
import ProposalRedline, { ProposalRedlineUpdate } from "work/proposals/redlining/proposal-redline";
import ReviewerApprovalBadge from "work/proposals/reviewers/_views/reviewer-approval-badge";
import ReviewerLeaderMessage from "work/proposals/reviewers/_views/reviewer-leader-message";
import ReviewerSelector from "work/proposals/reviewers/_views/reviewer-selector";
import { useRequestProposalReReview } from "work/proposals/reviewers/_hooks/use-request-proposal-re-review";
import PendingReviewerReminders from "work/proposals/reviewers/_views/pending-reviewer-reminders";
import { pendingProposalReviewers } from "work/proposals/_views/_shared/proposal-pending-review-warning";
import { getReviewerActionState } from "work/proposals/reviewers/_views/reviewer-selection";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";
import { useGetWorkAgreementByIdQuery } from "work/work-agreements/_api/work-agreement-api";
import useReplacedWorkDepartures from "work/proposals/_views/_shared/use-replaced-work-departures";
import TutorialTarget from "_shared/tutorials/tutorial-target";
// #endregion

// #region Styled Components
// #endregion

type ActiveProposalAction = "revise" | "review" | "view";

/** An unsent revision this reader may delete, and the hired work agreement it revises (#7088). */
interface DiscardableRevision {
  id: Guid;
  workAgreementId: Guid;
}
const ActiveProposalActions = new Set<ActiveProposalAction>(["revise", "review", "view"]);
const EMPTY_COMMENT_THREADS: CommentThread[] = [];

export default function ActiveProposalPage() {
  // #region Refs
  const navigate = useNavigate();
  const location = useLocation();
  const { action: actionParam, id: idParam, pageTab: pageTabParam } = useParams();
  const dispatch = useAttorneyHubDispatch();
  const confirm = useConfirmDialog();
  // The redline handler of the latest render, for work that finishes after a dialog: its closure
  // holds the proposal as it was when it was drawn.
  const handleProposalRedlineChangedRef = useRef(handleProposalRedlineChanged);
  useEffect(() => {
    handleProposalRedlineChangedRef.current = handleProposalRedlineChanged;
  });
  const reconcileStaffing = useReconcileFeeScheduleStaffing();
  const session = useSession();
  const [isOnboardingProposal] = React.useState(session.isRestrictedOnboarding);
  // What narrows the controls is the pane, not the window: the navigation drawer and this page's own
  // right-hand drawer both take width a media query cannot see (#4964).
  const { paneRef, paneWidth, isCompact: paneIsNarrow } = useWorkPaneSize();
  const paneTheme = useWorkPaneTheme(paneWidth);
  const canSeatSidePanel = useProposalPageCanSeatSidePanel();

  const pageAction: ActiveProposalAction = actionParam as ActiveProposalAction;

  // #initialization checks
  if (!ActiveProposalActions.has(pageAction)) {
    throw new Response("Not Found", { status: HttpStatusCode.NotFound });
  }
  if (!idParam) {
    throw new Response("Not Found", { status: HttpStatusCode.NotFound });
  }

  // #region State
  const [isSaving, setIsSaving] = React.useState(false);
  const [publishedRatesOfferDismissed, setPublishedRatesOfferDismissed] = React.useState(false);
  const [isPreparingSubmission, setIsPreparingSubmission] = React.useState(false);
  // The content this page's own submission sent: its negotiation, and the content version the
  // accepted command answered with. A submission is not something the page can re-derive from local
  // state, and every other term in `submitChangesDisabled` is local state -- so once the mutation
  // settles the button re-arms off whatever the page was rendering when it was pressed, and the next
  // press sends a second revision carrying the same content as the first (#5238).
  const [sentProposalSubmission, setSentProposalSubmission] = React.useState<{
    negotiationId: string;
    contentVersion: number;
  } | null>(null);
  const [newFeeCategoryFormKey, setNewFeeCategoryFormKey] = React.useState(NEW_FEE_CATEGORY_DRAWER_KEY);
  const [panelState, dispatchPanelState] = useProposalPagePanels<
    FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
  >();
  const {
    selectedReviewField: userSelectedReviewField,
    commentsTitle: commentsDrawerTitle,
    commentsOpen: commentsDrawerIsOpen,
    addFeeOpen: isAddFeePanelOpen,
    existingFeeOpen: isExistingFeePanelOpen,
    teamSelectionOpen: isTeamSelectionPanelOpen,
    currentCategory,
    reviewerAnchor: reviewerSelectorAnchor,
    isManagingReviewers,
    traversalStop,
    commentToScrollTo,
    fieldArrivals,
  } = panelState;

  // #region Global Vars
  const proposalId = useMemo(() => new Guid(idParam), [idParam]);
  const isReadOnlyView = pageAction === "view" || !pageAction;
  const activeTab = pageTabParam as PageTab;
  const isTeamSelectionDrawerOpen = activeTab === PageTab.VendorTeam && isTeamSelectionPanelOpen;

  // #region Proposal Queries
  const [, requestRevisionResult] = useRequestProposalRevisionMutation();
  const [, approveProposalResult] = useApproveProposalMutation();
  const [, rejectProposalResult] = useRejectProposalMutation();
  const [, hireProposalResult] = useHireProposalMutation();
  const [, cancelProposalResult] = useCancelProposalMutation();
  const [quitProposalTeamAPICommand, quitProposalTeamResult] = useQuitProposalTeamMutation();
  const [, giveReviewerApprovalResult] = useGiveReviewerApprovalMutation();
  const [, submitProposalResult] = useSubmitProposalMutation();
  const [requestProposalRevisionNegotiation, requestProposalRevisionNegotiationResult] =
    useRequestProposalRevisionNegotiationMutation();
  const [submitProposalCollaborationDraft, submitProposalCollaborationDraftResult] =
    useSubmitProposalCollaborationDraftMutation();
  const [completeProposalReview, completeProposalReviewResult] = useCompleteProposalReviewMutation();
  const [approveProposalNegotiation, approveProposalNegotiationResult] = useApproveProposalNegotiationMutation();
  const [rejectProposalNegotiation, rejectProposalNegotiationResult] = useRejectProposalNegotiationMutation();
  const [cancelProposalNegotiation, cancelProposalNegotiationResult] = useCancelProposalNegotiationMutation();
  const [hireProposalNegotiation, hireProposalNegotiationResult] = useHireProposalNegotiationMutation();
  const [markProposalChangesSeen] = useMarkProposalChangesSeenMutation();
  const [deleteProposal] = useDeleteProposalMutation();
  // Set while an unsent revision is being discarded (#7088). It stops the page reading the envelope it
  // is deleting, and stops the redirects below from answering the deletion they will hear about.
  const [isDiscardingRevision, setIsDiscardingRevision] = useState(false);
  const isDiscardingRevisionRef = useRef(false);

  // The submission decision — what to send, to whom, and whether to publish comments — is asked once,
  // against the envelope the pre-submit save just acknowledged.
  const { resolveSubmission, submitProposalDialog } = useSubmitProposalDialog();

  const collaboration = usePersistedProposalSaveOrchestration(
    proposalId && !proposalId.isEmpty ? proposalId.value : undefined,
    { isDeleting: isDiscardingRevision },
  );
  // Deleted, or this viewer's access revoked, while they had it open — pushed over the websocket.
  const proposalWasDeleted = useSelector(getProposalIsNoLongerAvailable(proposalId));
  const savedProposalJson =
    collaboration.mode === "canonical" ? collaboration.envelope?.projection : undefined;
  const effectiveProposalJson =
    collaboration.mode === "canonical" ? collaboration.effectiveProposalJSON : undefined;
  const sessionUser = useSessionUser();
  const proposalAccess = useWhoHasAccess("proposals", "the proposal");
  const savedProposal = useMemo(() => {
    if (!savedProposalJson || isProposalLoadIssue(savedProposalJson) || !session.user) return null;
    if (collaboration.mode === "canonical" && collaboration.envelope) {
      return proposalFromCanonicalNegotiationJSON({
        proposalJSON: savedProposalJson,
        currentUser: session.user,
        resolutionStates: collaboration.resolutionStates,
        currentSubmissionId: collaboration.envelope.currentSubmissionId,
        viewingParty: collaboration.envelope.viewingParty,
      });
    }
    return Proposal.fromJSON({
      json: savedProposalJson,
      currentUser: session.user,
      options: { withRedline: true },
    });
  }, [
    collaboration.envelope,
    collaboration.mode,
    collaboration.resolutionStates,
    savedProposalJson,
    session.user,
  ]);
  // The saved proposal, never this reader's unsaved edits: a template holds what was saved (#6129).
  const saveAsProposalTemplate = useSaveAsProposalTemplateAction({
    proposal: savedProposal,
    canCreateProposals: sessionUser?.canCreateProposals() ?? false,
  });
  const proposal = useMemo(() => {
    if (!effectiveProposalJson || isProposalLoadIssue(effectiveProposalJson) || !session.user) return null;
    if (collaboration.mode === "canonical" && collaboration.envelope) {
      return proposalFromCanonicalNegotiationJSON({
        proposalJSON: effectiveProposalJson,
        // Every own-side line is measured against the shared checkpoint, never the saved draft (#6886).
        projectionBaseline: proposalRedlineBaselineJSON({
          projection: collaboration.envelope.projection,
          publishedResolutionStates: collaboration.envelope.publishedResolutionStates ?? [],
          currentSubmissionId: collaboration.envelope.currentSubmissionId,
          viewingParty: collaboration.envelope.viewingParty,
          agreedBaseline: collaboration.envelope.agreedBaseline,
        }),
        currentUser: session.user,
        resolutionStates: collaboration.resolutionStates,
        currentSubmissionId: collaboration.envelope.currentSubmissionId,
        viewingParty: collaboration.envelope.viewingParty,
      });
    }
    return Proposal.fromJSON({
      json: effectiveProposalJson,
      currentUser: session.user,
      options: { withRedline: true },
    });
  }, [
    collaboration.envelope,
    collaboration.mode,
    collaboration.resolutionStates,
    effectiveProposalJson,
    session.user,
  ]);
  // #region Comments Queries
  // Comments are Messaging-native: a comment is a Message in a topic-filtered Forum keyed by the
  // durable negotiationId (stable across submission checkpoints), NOT the per-submission projection id.
  // We feed the comment layer a proposal whose id is the negotiationId so every forum/thread/store key
  // aligns on that durable id. That store is exactly what the `messages` Ably middleware feeds, so
  // reading it here is what restores comment realtime.
  const negotiationGuid = useMemo(() => {
    const id = collaboration.mode === "canonical" ? collaboration.envelope?.negotiationId : undefined;
    if (!id) return undefined;
    try {
      return new Guid(id);
    } catch {
      return undefined;
    }
  }, [collaboration.mode, collaboration.envelope?.negotiationId]);
  // Held through an envelope reload rather than re-derived on every render: the pane is rendered only
  // while this is present, and the composer inside it loses what is being typed if it is unmounted and
  // remounted underneath. See `useCommentPaneProposal`.
  const commentProposal = useCommentPaneProposal({
    routeProposalId: proposalId,
    proposal,
    negotiationId: collaboration.mode === "canonical" ? collaboration.envelope?.negotiationId : undefined,
    envelopeIsLoading: collaboration.isLoading,
  });
  // The negotiation this proposal revises, when it revises one: its conversation leads the Comments
  // pane, and its history each field's. See `useRevisedNegotiation`.
  const {
    negotiationId: carriedOverNegotiationId,
    refused: carriedOverRefused,
    fieldHistory: carriedOverFieldHistory,
  } = useRevisedNegotiation(proposal?.revisesProposalId?.value);
  // The header's Activity popover (#5648). The leader's row, and its history, is drawn only while the
  // team has a leader the viewer may see; otherwise the leader's changes are activity.
  const [activityAnchor, setActivityAnchor] = useState<HTMLElement | null>(null);
  const hasVisibleTeamLeader = Boolean(
    proposal && MaskedTeam.isPresentNotMasked(proposal.vendorTeam) && proposal.vendorTeam.leader,
  );
  const activityCoverage = useMemo(
    () => proposalActivityCoverage({ hasTeamLeader: hasVisibleTeamLeader }),
    [hasVisibleTeamLeader],
  );

  const loadedCommentThreads = useSelector(getCommentThreadsByProposal(commentProposal));
  const commentThreads = loadedCommentThreads ?? EMPTY_COMMENT_THREADS;
  // Selected as ids rather than as the comment list, so the page renders again when a conversation's
  // oldest unread comment changes and not on every write to it. One per conversation: each is a stop.
  const oldestUnreadNegotiationComment = useSelector((state: RootState) =>
    oldestUnreadCommentId(getCommentsByProposal(commentProposal?.id)(state), session.user?.id, true),
  );
  // The pane's own rule, so no count or stop is left for a conversation it does not draw (#6515).
  const negotiationIsOffered = offersNegotiation(commentProposal ?? proposal);
  const oldestUnreadAllStakeholdersComment = negotiationIsOffered ? oldestUnreadNegotiationComment : undefined;
  const oldestUnreadOwnTeamComment = useSelector((state: RootState) =>
    oldestUnreadCommentId(getCommentsByProposal(commentProposal?.id)(state), session.user?.id, false),
  );
  const isLoading = collaboration.isLoading;

  // #region Permissions Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const permissionsLoadState = useSessionPermissionsLoadState();

  const hiredWorkAgreementQuery = useGetWorkAgreementByIdQuery(
    !isOnboardingProposal &&
      proposal?.isHired &&
      proposal.workAgreementId &&
      sessionPermissions?.includesAccessToWork
      ? { workAgreementId: proposal.workAgreementId.value, session }
      : skipToken,
  );

  const replacedWorkDepartures = useReplacedWorkDepartures(proposal?.id, proposal?.replacesWorkAgreementId);

  // A hire grants every party member access to the work agreement it ends on, and the realtime notice
  // that announces the hire says nothing about permissions. A viewer who held no work access before --
  // the vendor leader of a firm with no other agreement -- therefore carries a session permission
  // cache the hire has just made stale, and the work agreement page answers `NotAuthorized` from it.
  // Read the permissions again, once, when this page first sees that the proposal is hired.
  const hiredPermissionsWereRefreshedRef = useRef(false);
  useEffect(() => {
    if (!proposal?.isHired || isOnboardingProposal) return;
    if (sessionPermissions?.includesAccessToWork !== false) return;
    if (hiredPermissionsWereRefreshedRef.current) return;
    hiredPermissionsWereRefreshedRef.current = true;
    dispatch(populateSessionPermissions({ session }));
  }, [dispatch, isOnboardingProposal, proposal?.isHired, session, sessionPermissions]);

  // #region Globals Continued
  const appDirectedNavigationPathRef = useRef<string | null>(null);
  const proposalLifecycleActionInFlightRef = useRef(false);

  // A canonical submission creates a successor checkpoint while retaining the
  // negotiation identity and local queue. Realtime/refetch can therefore hand
  // this mounted old-id route the new projection directly; keep the URL in
  // lockstep without waiting for legacy `isCurrentRevision` metadata.
  useEffect(() => {
    const canonicalProposalId = collaboration.mode === "canonical" ? collaboration.envelope?.projection.id : undefined;
    if (!canonicalProposalId || canonicalProposalId === proposalId.value) return;

    // The query goes along, so a link that opened the Comments pane still opens it on the successor.
    const successorPath = `/proposals/active/${pageAction}/${canonicalProposalId}/${pageTabParam ?? PageTab.Details}${location.search}`;
    appDirectedNavigationPathRef.current = successorPath;
    navigate(successorPath, { replace: true });
  }, [
    collaboration.envelope?.projection.id,
    collaboration.mode,
    location.search,
    navigate,
    pageAction,
    pageTabParam,
    proposalId.value,
  ]);

  /**
   * An unsent proposal that revises nothing is written in the draft editor, where the proposals list
   * opens it. This editor redlines against agreed terms, and such a proposal has none, so it waited
   * here on a "Loading..." that never ended: the continuation a restricted handover or quit opens,
   * reached from the notice that names it (#6094). The server's available actions decide, as they
   * do for the list: Edit without Revise is a plain draft.
   */
  const viewerId = session.user?.id;
  const opensInTheDraftEditor =
    !isOnboardingProposal &&
    Boolean(proposal?.id) &&
    proposal?.isSubmitted === false &&
    proposal.availableActions[ProposalAction.Edit].some((userId) => userId.isEqualTo(viewerId)) &&
    !proposal.availableActions[ProposalAction.Revise].some((userId) => userId.isEqualTo(viewerId));
  const draftEditorProposalId = opensInTheDraftEditor ? proposal?.id?.value : undefined;
  useEffect(() => {
    if (!draftEditorProposalId) return;
    // The query goes along, so a link that opened the Comments pane still opens it in the draft editor.
    const draftPath = `/proposals/drafts/edit/${draftEditorProposalId}/${pageTabParam ?? PageTab.Details}${location.search}`;
    appDirectedNavigationPathRef.current = draftPath;
    navigate(draftPath, { replace: true });
  }, [draftEditorProposalId, location.search, navigate, pageTabParam]);

  const isSubmitting =
    isPreparingSubmission ||
    requestRevisionResult.isLoading ||
    approveProposalResult.isLoading ||
    approveProposalNegotiationResult.isLoading ||
    rejectProposalNegotiationResult.isLoading ||
    cancelProposalNegotiationResult.isLoading ||
    hireProposalNegotiationResult.isLoading ||
    rejectProposalResult.isLoading ||
    hireProposalResult.isLoading ||
    cancelProposalResult.isLoading ||
    quitProposalTeamResult.isLoading ||
    giveReviewerApprovalResult.isLoading ||
    submitProposalResult.isLoading ||
    requestProposalRevisionNegotiationResult.isLoading ||
    submitProposalCollaborationDraftResult.isLoading ||
    completeProposalReviewResult.isLoading;

  const canSubmitApproval = Boolean(
    session.user?.canApprove(proposal) && proposal?.redline?.isResolved && !proposal.redline.hasRevisions,
  );
  const canHireProposal =
    collaboration.mode === "canonical" ? Boolean(collaboration.capabilities?.canHire) : false;

  const issues = useMemo(
    () => (proposal?.redline
      ? ProposalIssues.fromRedline(proposal.redline, {
          clientTeam: proposal.clientTeam,
          vendorTeam: proposal.vendorTeam,
          negotiable: proposal.negotiable?.value,
          // A change order preserves the hired agreement's historical baseline on submission.
          allowPastStartDate: Boolean(proposal.replacesWorkAgreementId),
        })
      : undefined),
    [proposal],
  );
  const approvalIssues = useMemo(
    () => (proposal?.redline && (canSubmitApproval || canHireProposal)
      ? ProposalIssues.fromRedline(proposal.redline, {
          clientTeam: proposal.clientTeam,
          vendorTeam: proposal.vendorTeam,
          negotiable: proposal.negotiable?.value,
          // Approval and hire accept dates already entered by a party to the proposal.
          allowPastStartDate: true,
        })
      : issues),
    [proposal, canSubmitApproval, canHireProposal, issues],
  );
  const persistedRedline = proposal?.persistedRedline;
  const selectedFeeScheduleCategories = useMemo(() => {
    const selectedCategories = proposal?.workAgreement?.feeSchedule ?? [];
    const feeScheduleRedline = proposal?.redline?.feeSchedule;
    const redlineCurrentEntries =
      Masked.isPresentNotMasked(feeScheduleRedline) && Array.isArray(feeScheduleRedline.currentEntries)
        ? feeScheduleRedline.currentEntries
        : [];

    if (Masked.isMasked(selectedCategories)) return redlineCurrentEntries;

    return uniqBy([...selectedCategories, ...redlineCurrentEntries], (category) => category.id.value);
  }, [proposal?.redline?.feeSchedule, proposal?.workAgreement?.feeSchedule]);

  const fieldsPendingReviewForActiveTab = useMemo(() => {
    const proposalFieldCategories = Object.values(ProposalFieldCategory);
    const proposalFieldNames = Object.values(ProposalFieldName);
    let activeCategory: ProposalFieldCategoryType | undefined;
    switch (activeTab) {
      case PageTab.Details:
        activeCategory = ProposalFieldCategory.Details;
        break;
    case PageTab.VendorTeam:
      activeCategory = ProposalFieldCategory.Team;
      break;
      case PageTab.ClientTeam:
        activeCategory = ProposalFieldCategory.Details;
        break;
      case PageTab.FeeSchedule:
        activeCategory = ProposalFieldCategory.FeeSchedule;
        break;
      case PageTab.Conflicts:
        activeCategory = ProposalFieldCategory.Conflicts;
        break;
      case PageTab.Policies:
        activeCategory = ProposalFieldCategory.Policies;
        break;
      case PageTab.Discount:
        activeCategory = ProposalFieldCategory.Discount;
        break;
    }
    const tabIssues: ProposalField[] = uniqBy(
      issues?.entries.map((issue) => issue.field).filter((field) => field.category === activeCategory) ?? [],
      (field) => field.toJSON(),
    );

    return chain(tabIssues)
      .uniqBy((field) => `${field.category}-${field.name}-${field.id?.value ?? ""}`)
      .sortBy([
        (field) => proposalFieldCategories.indexOf(field.category),
        (field) => proposalFieldNames.indexOf(field.name),
      ])
      .value();
  }, [activeTab, issues?.entries]);

  const activeReviewField = useMemo(() => {
    if (userSelectedReviewField?.category === proposalFieldCategoryFromPageTab(activeTab)) {
      return userSelectedReviewField;
    }
    return fieldsPendingReviewForActiveTab[0];
  }, [activeTab, userSelectedReviewField, fieldsPendingReviewForActiveTab]);

  // Arriving at a stop — a new tab, or any arrival at a field — scrolls it into view once it is drawn.
  // A stop on another tab is selected a render before that tab is shown, and the tab being left would
  // scroll to its own first issue in between; the arrival waits for the stop's tab instead.
  const arrivingAtAnotherTab =
    userSelectedReviewField !== undefined &&
    userSelectedReviewField.category !== proposalFieldCategoryFromPageTab(activeTab);
  const { traverserStopScope, attachTraverserStopScope } = useTraverserStopScope(
    arrivingAtAnotherTab ? undefined : `${activeTab}:${fieldArrivals}`,
  );

  const isReviewing = useMemo(() => {
    return (
      [...(proposal?.clientReviewers ?? []), ...(proposal?.vendorReviewers ?? [])].some((reviewer) =>
        reviewer.userId.isEqualTo(session.user?.id),
      ) ?? false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal?.clientReviewersCacheKey, proposal?.vendorReviewersCacheKey, session.user?.id]);

  const reviewerHasCurrentApproval = (reviewer: ProposalReviewer) =>
    collaboration.mode === "canonical" && collaboration.envelope
      ? isReviewerApprovalCurrent(collaboration.envelope, {
          userId: reviewer.userId.value,
          legalEntityId: reviewer.entityId.value,
        })
      : Boolean(reviewer.dateApproved);
  const allReviewers: ProposalReviewer[] = [
    ...(proposal?.clientReviewers ?? []),
    ...(proposal?.vendorReviewers ?? []),
  ];
  const hasUserApprovedReview = allReviewers.some(
    (reviewer) => reviewer.userId.isEqualTo(session.user?.id) && reviewerHasCurrentApproval(reviewer),
  );
  // What the team leader wrote when they asked this viewer to review (#7075).
  const currentUserReviewer = allReviewers.find((reviewer) => reviewer.userId.isEqualTo(session.user?.id));
  // Canonical completion is a versioned endorsement, not an edit lock. Edit
  // access remains an independent server capability after a review is complete.
  const reviewCompletionLocksEditing = collaboration.mode !== "canonical" && hasUserApprovedReview;
  const reviewers = (session.context?.viewingAsVendor ? proposal?.vendorReviewers : proposal?.clientReviewers) ?? [];
  const originalReviewers =
    (session.context?.viewingAsVendor ? savedProposal?.vendorReviewers : savedProposal?.clientReviewers) ?? [];
  // What each tab is holding that the counterparty cannot see yet: this actor's own unsaved changes,
  // and their saved changes that no submission has carried. Distinct from the unviewed badge beside
  // them, which counts what somebody else did.
  const tabPendingCounts = useProposalTabPendingCounts({
    pendingOperations: collaboration.pendingChanges.operations,
    reviewerCount: reviewers.length,
    isReviewing,
    submissionEnvelope: collaboration.mode === "canonical" ? collaboration.envelope : undefined,
  });
  useProposalPageAssistantScreenContext({
    page: "Active proposal",
    proposal,
    tab: activeTab,
    openPanel: panelState.openSidePanel,
    commentsTitle: commentsDrawerTitle,
    action: pageAction,
    unsavedChanges: { count: tabPendingCounts.unsavedChangeCount, status: tabPendingCounts.unsavedChangeStatus },
    unsubmittedChanges: {
      count: tabPendingCounts.unsubmittedChangeCount,
      status: tabPendingCounts.unsubmittedChangeStatus,
    },
  });
  // Resolves people and collection items for every change description this page renders — the save
  // confirmation, the submit dialog, and the field history all read from the same context.
  const changeDescriptionContext = useProposalChangeDescriptionContext(
    collaboration.effectiveProposalJSON ?? collaboration.envelope?.projection,
    collaboration.envelope?.projection,
    collaboration.envelope?.changeLines,
  );
  const {
    hasActualSaveUpdates: legacyHasActualSaveUpdates,
    isDirty: legacySaveStateIsDirty,
    proposalHasUnsavedRedlineChanges: legacyProposalHasUnsavedRedlineChanges,
  } = Proposal.getSaveState({
    proposal,
    savedProposal,
  });
  const proposalSavePatchPayload = useMemo(
    () =>
      getProposalSavePatchPayload({
        originalProposal: savedProposal,
        updatedProposal: proposal,
        session,
      }),
    [proposal, savedProposal, session],
  );
  const proposalHasPatchableSaveChanges =
    collaboration.mode === "canonical"
      ? collaboration.hasPendingChanges
      : proposalSavePatchPayload === null
        ? legacySaveStateIsDirty
        : proposalSavePatchPayload.length > 0;
  const hasActualSaveUpdates =
    collaboration.mode === "canonical" ? collaboration.hasPendingChanges : legacyHasActualSaveUpdates;
  const proposalHasUnsavedRedlineChanges =
    collaboration.mode === "canonical"
      ? collaboration.hasPendingChanges
      : legacyProposalHasUnsavedRedlineChanges;
  // Comments are deliberately absent from this. The page used to count the actor's staged comments as
  // unsaved work, which armed the discard-on-navigation prompt and offered a Save whose only subject was
  // a comment. A comment is written when it is written, so leaving the page can no longer lose one and
  // there is nothing about the conversation for a proposal save to be responsible for.
  const isDirty = proposalHasPatchableSaveChanges;
  const hasPendingReviewerChanges = proposalHasUnsavedRedlineChanges;
  const reviewCompletionSharePlan = buildProposalSaveSharePlan({
    operations: collaboration.pendingChanges.operations,
    reviewerChanges: collaboration.pendingChanges.reviewerChanges,
    audienceLabel: proposalSaveAudienceLabel({
      reviewerCount: reviewers.length,
      isReviewing,
    }),
    context: changeDescriptionContext,
  });
  const canEditProposal =
    collaboration.mode === "canonical"
      ? Boolean(collaboration.capabilities?.canEditDraft)
      : Boolean(session.user?.canRedline(proposal));
  // Whether the submission under review still carries counterparty changes this viewer has not
  // decided yet. This is what makes a bulk Accept All / Reject All meaningful, and it is read from
  // the effective resolution states so a locally queued decision retires its own target.
  const currentSubmissionId = collaboration.envelope?.currentSubmissionId;
  const viewingParty = collaboration.envelope?.viewingParty;
  const hasUnresolvedIncomingChanges = useMemo(() => {
    if (!currentSubmissionId || !viewingParty) return false;
    return collaboration.resolutionStates.some(
      (state) =>
        state.submissionId === currentSubmissionId &&
        state.disposition === "Unresolved" &&
        isProposalResolutionTargetResolvable(state, viewingParty),
    );
  }, [collaboration.resolutionStates, currentSubmissionId, viewingParty]);
  // Who Submit Changes reaches, looked up as the page renders so the dialog can name them by person
  // and firm the moment it opens (#5813).
  const submitRecipient = useProposalSubmitRecipient(
    proposalSubmitRecipientMember(proposal?.workAgreement, viewingParty),
  );
  const canManageReviewers =
    collaboration.mode === "canonical"
      ? Boolean(collaboration.capabilities?.canManageReviewers)
      : !isReviewing;
  const reReview = useRequestProposalReReview({
    envelope: collaboration.mode === "canonical" ? collaboration.envelope : undefined,
    viewerScope: collaboration.viewerScope,
    canManageReviewers,
  });
  const canRejectNegotiation =
    collaboration.mode === "canonical" ? Boolean(collaboration.capabilities?.canReject) : false;
  const canCancelNegotiation =
    collaboration.mode === "canonical" ? Boolean(collaboration.capabilities?.canCancel) : false;
  const canApproveAndHire = Boolean(canSubmitApproval && session.user?.isClient(proposal));
  const approvalActionLabel = canApproveAndHire ? "Approve & Hire" : "Submit Approval";
  // Approving is the point of no return: the server validates an approval against
  // `WorkAgreementValidationState.Hiring` (Proposal.ValidateApprovalAsync), the same rules the later
  // Hire re-runs. So EVERY issue blocks approval, Warnings included — a Warning here is not an
  // advisory, it is precisely a rule that holds at Hiring but not at Submitting: an empty fee
  // schedule, a deferred fee, an hourly category with no vendor team member, conflicts documents that
  // are neither attached nor waived. Save and Submit Changes correctly use the Critical-only grading
  // (`canSave`/`canSubmit`), because the server validates those at `Submitting` and permits all of the
  // above. Relaxing this gate to `canSubmit` produced the worst possible outcome: an enabled button
  // whose click the server answered with a 400.
  const approvalIsBlockedByIssues = approvalIssues === undefined || approvalIssues.entries.length > 0;
  const approvalActionTooltip = approvalIsBlockedByIssues
    ? "Resolve Issues to Approve Proposal"
    : canApproveAndHire
      ? "Approve and Hire"
      : approvalActionLabel;
  // An unsubmitted draft that is nonetheless a revision of something: the retired per-submission
  // chain's superseded proposal, or — the case the revise flow actually produces — a hired work
  // agreement. Reading only `supersedes` meant a work agreement revision never counted as one here.
  const proposalIsAwaitingSubmissionRevisionDraft = Boolean(
    proposal?.status === ProposalStatus.AwaitingSubmission &&
      (proposal.supersedes || proposal.revisesHiredWorkAgreement),
  );
  // Outgoing (blueline) work to submit is this party's own SAVED-but-unpublished changes plus any
  // unsaved edits. The redline projection only carries INCOMING opposing-party changes, so gating
  // submit on `redline.*`/`isModifiedByRedline` (as the legacy path does) reads false the moment a
  // superseding revision is saved — leaving Submit stuck disabled. In canonical mode use the same
  // publishable-changes signal the submit dialog's change list is built from.
  // `hasPendingContentChanges`, never `hasPendingChanges`: the staged client team roster is unsaved
  // work the page's Save writes, but it is reference state rather than negotiated content (see
  // ARCHITECTURE.md). Counting it here armed Submit over a roster-only edit, and the handler saves
  // the roster and then sends the submission command regardless — a submission with nothing in it
  // for the counterparty to negotiate (#5276).
  const proposalHasSubmittableRevisionChanges =
    collaboration.mode === "canonical"
      ? collaboration.hasPendingContentChanges ||
        (collaboration.envelope ? proposalPublishableChanges(collaboration.envelope).length > 0 : false)
      : Boolean(
          proposalIsAwaitingSubmissionRevisionDraft
            ? proposal?.redline?.hasChanges || proposal?.redline?.hasRevisions
            : proposal?.isModifiedByRedline,
        );
  // This party's revision is already sent, and no content has moved since. Two things release it, and
  // each is a report that there is something to send again: content queued locally, or a negotiation
  // whose content head has advanced past what the submission carried — this party saving new work, or
  // the counterparty answering with theirs. Neither is the redline the submission was sent from,
  // which goes on reading as changed for as long as the page renders the envelope it was pressed on.
  //
  // The proposal's STATUS cannot be the gate on its own, although it is the negotiation's own state:
  // Revise stays available to BOTH parties in every non-archived status — see
  // `AwaitingApprovalByClientStatus.DetermineAvailableActions` — so `capabilities.canSubmit` is still
  // true for the party that has just sent a revision, which is precisely the press this closes.
  const proposalRevisionIsAlreadySent =
    collaboration.mode === "canonical" &&
    sentProposalSubmission !== null &&
    collaboration.envelope?.negotiationId === sentProposalSubmission.negotiationId &&
    collaboration.envelope.contentVersion <= sentProposalSubmission.contentVersion &&
    !collaboration.hasPendingContentChanges;
  let submitChangesTooltip = "Submit Changes";
  if (proposalRevisionIsAlreadySent) {
    submitChangesTooltip = "Changes already sent";
  } else if (!proposalHasSubmittableRevisionChanges) {
    submitChangesTooltip = "No changes to submit";
  } else if (!issues?.canSubmit) {
    submitChangesTooltip = "Resolve Issues to Submit";
  }
  const totalReviewers = reviewers.length ?? 0;
  const pendingReviewCount = reviewers.filter((reviewer) => !reviewerHasCurrentApproval(reviewer)).length;
  const pageTitle = (() => {
    if (pageAction === "view") return "View";
    if (pageAction === "revise") return "Revise";
    if (pageAction === "review") return "Review";
    return "View";
  })();
  const disableSaveChanges = !isDirty;
  const [fieldHistoryToOpen, setFieldHistoryToOpen] = useState<ProposalField | undefined>(undefined);
  // The fee schedule's categories as its table draws them, sorted as the reader left it; undefined while
  // the table is not drawn (#6617).
  const [displayedFeeScheduleCategoryIds, setDisplayedFeeScheduleCategoryIds] = useState<
    readonly Guid[] | undefined
  >(undefined);
  const reviewerSelectorIsOpen = reviewerSelectorAnchor !== undefined;
  const proposalControlsArePaused = isManagingReviewers || reviewerSelectorIsOpen;
  const submitChangesDisabled =
    isSubmitting ||
    isLoading ||
    isSaving ||
    proposalControlsArePaused ||
    proposalRevisionIsAlreadySent ||
    !proposal?.redline?.isResolved ||
    Boolean(issues && !issues.canSubmit) ||
    !proposalHasSubmittableRevisionChanges;
  const hireProposalDisabled =
    isSubmitting ||
    isLoading ||
    isSaving ||
    proposalControlsArePaused ||
    Boolean(proposal?.redline?.hasChanges) ||
    approvalIsBlockedByIssues ||
    !canHireProposal;
  const showsSubmitChanges =
    !isReadOnlyView &&
    Boolean(session.user?.canRevise(proposal)) &&
    (!session.user?.canApprove(proposal) || Boolean(proposal?.redline?.hasRevisions));
  const showsHire = !canSubmitApproval && !isReviewing && !isReadOnlyView && Boolean(session.user?.isClient(proposal));
  // The one action that moves the proposal on from where it stands is filled; the rest stay outlined
  // (#5973). Approving and submitting changes never show together, and Hire waits on there being no
  // changes, so Hire leads once it can be pressed and Submit Changes leads while there are changes to
  // send. A reviewer's Submit Review is filled already and leads alone. Accept All is left outlined on
  // purpose: deciding every incoming change at once is a shortcut, not the step the page asks for.
  const primaryAction: "approve" | "hire" | "submitChanges" | undefined = isReviewing
    ? undefined
    : canSubmitApproval
      ? "approve"
      : showsHire && !hireProposalDisabled
        ? "hire"
        : showsSubmitChanges
          ? "submitChanges"
          : showsHire
            ? "hire"
            : undefined;
  const disableHeaderSave = isLoading || isSaving || disableSaveChanges;
  const disableIssueTraverser = isSubmitting || isSaving || proposalControlsArePaused;
  // The field whose change history the traverser asked to open, published to the tabs by context.
  const fieldHistoryRequest = {
    field: fieldHistoryToOpen,
    onHonored: () => setFieldHistoryToOpen(undefined),
  };
  // The fee schedule's rows in the order its table draws them, so Prev / Next walks down it as the
  // reader sorted it (#6617). While the table is not drawn, the schedule's own order, which is the
  // order the table opens in.
  const feeScheduleTraversalRowOrder = feeScheduleRowOrder(
    displayedFeeScheduleCategoryIds ?? selectedFeeScheduleCategories.map((category) => category.id),
  );
  // Fields whose only pending item is an unread counterparty change, so "Next/Prev Change" stops there.
  const unreadChangeFields = proposalUnreadChangeFields(
    collaboration.mode === "canonical" ? collaboration.envelope : undefined,
    session.user?.id?.value,
  );
  let saveButtonTooltip = "Save reviewer changes.";
  if (hasActualSaveUpdates) {
    // Changes only. Save used to promise "Save changes and comments" because it carried both; comments
    // are written as they are made, so it would be promising something it does not do.
    saveButtonTooltip = reviewers.length
      ? "Save changes. Reviewers will receive the updates."
      : "Save changes.";
  }
  if (!isDirty) {
    saveButtonTooltip = "No changes to save.";
  }
  const submitReviewTooltip = (() => {
    if (hasUserApprovedReview) return "Review complete. You can continue to add comments.";
    if (!hasPendingReviewerChanges) return "Complete review.";
    return "Save changes and complete review.";
  })();


  // Each conversation's count, as its tab in the pane shows it: the traverser stops once at each one
  // with any (#5824). The Comments button shows both together.
  const allStakeholdersUnreadCount = useSelector(
    getConversationUnreadCount({
      commentThreads,
      proposalId: commentProposal?.id ?? undefined,
      isExternal: true,
      readerId: session.user?.id,
    }),
  );
  const unreadCommentCounts = {
    allStakeholders: negotiationIsOffered ? allStakeholdersUnreadCount : 0,
    ownTeam: useSelector(
      getConversationUnreadCount({
        commentThreads,
        proposalId: commentProposal?.id ?? undefined,
        isExternal: false,
        readerId: session.user?.id,
      }),
    ),
  };
  const unreadCommentCount = unreadCommentCounts.allStakeholders + unreadCommentCounts.ownTeam;

  const shouldDisableTabs = isLoading || isSaving || proposalControlsArePaused;
  // #endregion

  // #region Handle Traverse
  const handleTraverseToNewField = (
    field?: ProposalField,
    source: ProposalFieldTraversalSource = ProposalFieldTraversalSource.Field,
  ) => {
    if (!field) return;
    const newTab = pageTabFromProposalFieldCategory(field.category);
    if (!newTab) return;
    // A stop on an unread change is a stop about that change, so it opens that field's own change
    // history rather than the proposal's one conversation, which is what the pane shows and which is
    // not what the reader was sent here to read. Asked BEFORE acknowledging, since acknowledging is
    // what makes the change no longer unread.
    //
    // Only "Prev / Next Change" opens it. This handler is also every field's own traverse-in — focusing
    // a field, or clicking its Edit button, reports itself here — and a reader who reached for Edit
    // asked to type, not to read history. Opening it for them put a panel over the field on the click
    // that was meant to start editing it.
    const stoppedOnUnreadChange =
      source === ProposalFieldTraversalSource.Traverser && proposalFieldHasUnreadChange(field);
    // Arriving at a field never opens the pane (#6625); a focus or an Edit press keeps an open one open
    // on that field. An issue stop answers itself where it lands: setting the review field below scrolls
    // the highlighted row into view.
    const keepCommentsPaneOpen = proposalTraversalKeepsCommentsPaneOpen({
      source,
      field,
      commentsPaneIsOpen: commentsDrawerIsOpen,
    });
    // Stopping on a field is also reading it: surface what the stop is about and acknowledge what it
    // surfaced, so pressing Next repeatedly walks the unread changes down to none and the traverser
    // disables itself — the same "opening it is the acknowledgement" rule the field history control
    // already follows.
    markTraversedFieldChangesSeen(field);
    dispatchPanelState({
      type: "setSelectedReviewField",
      field,
      closePanels: true,
      openComments: keepCommentsPaneOpen,
      traversed: source === ProposalFieldTraversalSource.Traverser,
    });
    if (stoppedOnUnreadChange) {
      setFieldHistoryToOpen(field);
    } else {
      // Moving on answers any standing request: leaving the previous field's history asked-for would
      // hold that panel open over a field the reader has already left.
      setFieldHistoryToOpen(undefined);
    }
    // Surfacing the pane marks nothing read (#5824): a comment is read when it is scrolled into view, on
    // the tab it belongs to.
    navigate(`/proposals/active/${pageAction}/${proposalId?.value}/${newTab}`);
  };

  /**
   * A deliberate "Prev / Next Change" press — the only traversal that opens the stop's own history.
   *
   * Both traversers on the page share it. The footer one used to be handed `handleTraverseToNewField`
   * raw, so its presses arrived as the field's own traverse-in and it answered an unread-change stop
   * with the conversation pane instead of the change history — the same button behaving differently
   * depending on which end of the page it was clicked at, exactly as it once did over
   * `unreadChangeFields`.
   */
  const handleTraverseFromTraverser = (field?: ProposalField) =>
    handleTraverseToNewField(field, ProposalFieldTraversalSource.Traverser);

  /**
   * The "Prev / Next" stops that are not fields: one for each conversation with unread comments, after
   * every field and before the walk wraps to the first (#5824). It opens the pane on that conversation's
   * tab, scrolled to its oldest unread comment — where the reader's unread reading begins. Arriving
   * marks nothing read; the comments the reader then sees are.
   */
  const handleTraverseToUnreadComments = (isExternal: boolean) => {
    dispatchPanelState({
      type: "arriveAtUnreadComments",
      isExternal,
      commentToScrollTo: isExternal ? oldestUnreadAllStakeholdersComment : oldestUnreadOwnTeamComment,
    });
    setFieldHistoryToOpen(undefined);
  };

  function proposalFieldHasUnreadChange(field: ProposalField): boolean {
    const envelope = collaboration.mode === "canonical" ? collaboration.envelope : undefined;
    return proposalFieldUnread(envelope, field, session.user?.id?.value).unreadCount > 0;
  }

  const markTraversedFieldChangesSeen = (destinationField: ProposalField) => {
    const envelope = collaboration.mode === "canonical" ? collaboration.envelope : undefined;
    const field = proposalFieldToMarkSeenOnTraversal({
      sidePaneIsOpen: commentsDrawerIsOpen,
      currentField: activeReviewField,
      destinationField,
    });
    // A fee-schedule category acknowledges itself and not its siblings (#5118).
    const path = field && (proposalTimelineItemPathForField(field) ?? proposalTimelinePathForField(field));
    if (!envelope || !path) return;
    const { unreadCount, latestUnseenSequence } = proposalFieldUnread(envelope, field, session.user?.id?.value);
    if (unreadCount > 0) {
      void markProposalChangesSeen({
        negotiationId: envelope.negotiationId,
        path,
        throughStreamVersion: latestUnseenSequence,
      });
    }
  };

  const handleTeamSelectionDrawerOpenChange = useCallback(
    (open: boolean) => {
      dispatchPanelState({ type: "setTeamSelectionOpen", open });
    },
    [dispatchPanelState],
  );


  // #region Populate Comment Threads
  // Loads the negotiation's comment Forums (get-or-created by Topic) into the Forum/Message comment
  // store the drawer and per-field badges read from — and that the Ably `messages` middleware updates.
  useEffect(() => {
    if (!commentProposal?.id || isLoading || loadedCommentThreads) return;
    void dispatch(populateCommentThreadsByProposal({ proposal: commentProposal, session }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentProposal?.id?.value, isLoading, loadedCommentThreads, session]);

  // #region Account view switching
  /**
   * The subject this page last moved the account view for. Opening it puts the person in the party
   * they sit on, once; after that the view is theirs. Without this the effect below re-ran on every
   * new session -- including the one the account switcher publishes -- saw the "wrong" party again,
   * and switched straight back: a second PATCH undoing the first.
   */
  const accountViewAlignedFor = useRef<string | undefined | null>(null);
  useEffect(() => {
    // The server says which party this person sits on. Membership on the proposal cannot be read
    // instead: a canonical projection masks the other side's team, and `proposal` is only ever built
    // from a canonical envelope, so there is nothing to decide until that envelope arrives.
    if (collaboration.mode !== "canonical" || !collaboration.envelope) return;
    const subject = proposalId?.value;
    if (accountViewAlignedFor.current === subject) return;
    accountViewAlignedFor.current = subject;

    const accountType =
      collaboration.envelope.viewingParty === "Vendor" ? AccountType.Vendor : AccountType.Client;
    const alreadyViewingParty = session.context?.viewingAsVendor === (accountType === AccountType.Vendor);

    if (!alreadyViewingParty) void session.setAccountViewType(accountType);
  }, [collaboration.envelope, collaboration.mode, proposalId, session]);

  // #region Routing
  /**
   * An ended negotiation is Archived, and the archive redirect below sends every viewer of it to its
   * archived view — including the person who just ended it, once the refetch their own command triggers
   * lands. Reject and cancel navigate there too, so the two navigations agree whichever runs last.
   */
  function navigateToArchivedProposal() {
    // The query goes along, so a link that opened the Comments pane still opens it on the archived view.
    const archivedProposalPath = `/proposals/archived/view/${proposal?.id?.value ?? proposalId.value}/${activeTab ?? PageTab.Details}${location.search}`;
    appDirectedNavigationPathRef.current = archivedProposalPath;
    navigate(archivedProposalPath, { replace: true });
  }

  // Derived out here, not read inside the effect below, because the effect's dependency list is
  // written by hand: a field of `collaboration` that only the effect body reads is a dependency the
  // list does not name, and the effect then answers from a stale render.
  const proposalAccessWasDenied = collaboration.mode === "error" && isProposalAccessDeniedError(collaboration.error);

  useEffect(() => {
    // This page deleted the revision itself, and `handleDiscardRevision` says where to go next.
    if (isDiscardingRevisionRef.current) return;

    // Deleted (or access revoked) under the viewer. This arrives over the websocket, so no load state
    // changes — the canonical envelope keeps its last good projection and the editor would keep going.
    if (proposalWasDeleted) {
      enqueueSnackbar("This proposal is no longer available.", { variant: "warning" });
      navigate(proposalListPath(ProposalStageChip.Active), { replace: true });
      return;
    }

    if (proposal?.isCurrentRevision === false) {
      const latestRevisionId =
        proposal.latestRevisionId?.isEqualTo(proposal.id) === false
          ? proposal.latestRevisionId
          : proposal.supersededById;
      if (!latestRevisionId) return;

      // The latest revision is the same negotiation, and pending edits are held per negotiation, so
      // they move with the user and there is nothing to ask about.
      const latestRevisionPath = `/proposals/active/revise/${latestRevisionId.value}/details`;
      appDirectedNavigationPathRef.current = latestRevisionPath;
      navigate(latestRevisionPath, {
        replace: true,
      });
      return;
    }

    // A terminal "this proposal is gone" is an answer, so fall through to the redirect below; anything
    // else that is still resolving (or failing transiently) must not eject the viewer.
    if (collaboration.mode === "loading" || collaboration.isFetching) return;
    if (collaboration.mode === "error" && !isProposalNoLongerAvailableError(collaboration.error)) return;

    if (proposal?.isHired && isOnboardingProposal) {
      // The app shell owns this navigation after the authoritative user and
      // permission caches confirm that onboarding restrictions are gone.
      appDirectedNavigationPathRef.current = "/dashboard";
      return;
    }

    // A hire ends the negotiation and gives both parties the same next page: the work agreement it
    // created. Only the client leader who pressed Hire gets there from the command response, through
    // `navigateAfterHire`, which reads the work agreement id off that response and asks nothing else.
    // Every other viewer -- the vendor leader above all -- arrives here on the realtime push instead.
    // Waiting for `hiredWorkAgreementQuery` to succeed made the two disagree: the query is skipped
    // until this session's permissions report work access, and a hire grants the vendor that access
    // with no push that refreshes their permission cache, so for them the query never started and the
    // redirect never ran. The hired projection already names the work agreement, so redirect on that,
    // and hold only for a fetch that started and failed.
    if (proposal?.isHired && proposal.workAgreementId && !hiredWorkAgreementQuery.isError) {
      const hiredWorkAgreementPath = `/work/active/view/${proposal.workAgreementId.value}/details`;
      appDirectedNavigationPathRef.current = hiredWorkAgreementPath;
      navigate(hiredWorkAgreementPath, {
        replace: true,
      });
      return;
    }

    // A rejected proposal is a record, not a negotiation: its envelope still loads, so without this the
    // viewer lands on an editor whose every edit `handleProposalRedlineChanged` drops. Links to it are
    // minted without knowing its fate (roll-out letters, the `Work.Proposal` notification). A hired
    // proposal is Archived too, and belongs to the work agreement above; an onboarding session is held
    // on its current proposal by the route guard, which would send it straight back here.
    if (proposal?.isArchived && !proposal.isHired && !isOnboardingProposal) {
      navigateToArchivedProposal();
      return;
    }

    if (!session.user || !pageAction) {
      const missing = new Error("Unable to open proposal. Missing required parameters.");
      console.error(missing);
      showErrorSnackbar(missing, { doing: "opening the proposal", operation: "proposal.open" });
      navigate(proposalListPath(ProposalStageChip.Active), { replace: true });
      return;
    }

    // A failed read on the review route is not proof the reader was removed as a reviewer, so it says
    // what the server said, as every other route does (#6622).
    if (!proposal && proposalId && !proposalId.isEmpty) {
      if (idParam) {
        enqueueSnackbar(proposalAccessWasDenied ? PROPOSAL_ACCESS_DENIED_MESSAGE : "Proposal not found", {
          variant: "warning",
        });
      }

      navigate(proposalListPath(ProposalStageChip.Active), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    proposal,
    proposalWasDeleted,
    collaboration.mode,
    collaboration.isFetching,
    proposalAccessWasDenied,
    proposalId,
    idParam,
    pageAction,
    activeTab,
    session.user,
    sessionPermissions,
    hiredWorkAgreementQuery.isError,
    isOnboardingProposal,
  ]);

  // #region Handle Tab Change
  function handleTabChange(_event: React.ChangeEvent<object> | null, newTab: PageTab) {
    dispatchPanelState({ type: "closeTabScopedPanels" });
    navigate(`/proposals/active/${pageAction}/${proposalId?.value}/${newTab}`);
  }

  // #region Handle Manage Reviewers
  // The anchor is passed in rather than read off the event, because the control the selector hangs
  // from is not always this action's own button: once the secondary actions collapse it is the "More
  // Actions" button, and the menu item that was pressed is gone by the time the popover opens.
  function handleManageReviewers(anchor: HTMLButtonElement) {
    dispatchPanelState({
      type: "openReviewerSelector",
      anchor,
    });
  }

  async function runProposalLifecycleActionOnce(action: () => Promise<void>) {
    if (proposalLifecycleActionInFlightRef.current) return;
    proposalLifecycleActionInFlightRef.current = true;
    try {
      await action();
    } finally {
      proposalLifecycleActionInFlightRef.current = false;
    }
  }

  // #region Handle Submit
  async function handleSubmitRevision() {
    setIsPreparingSubmission(true);
    try {
      await runProposalLifecycleActionOnce(submitRevisionOnce);
    } finally {
      setIsPreparingSubmission(false);
    }
  }

  async function submitRevisionOnce() {
    if (!proposal?.redline) throw new Error("No proposal redline to submit.");

    try {
      // Reviewer changes are now resolved in-field as targets, so an outstanding
      // reviewer approval no longer gates or nags submission. The leader submits
      // whatever the current state is.
      //
      // One dialog, asked after the pre-submit save so it can name the real change list — see the
      // draft page's submit for why the flush comes first.
      if (!session.user) throw new Error("No user found for submitted revision.");

      if (collaboration.mode === "canonical") {
        const viewerScope = collaboration.viewerScope;
        if (!viewerScope) {
          throw new Error("Proposal collaboration viewer is not ready for submission.");
        }
        // A save that does not answer an id has already said why. Throwing here put a second error,
        // "Failed to save proposal before submission.", beside it (#6628, #6614).
        const savedProposalId = await handleSaveChanges({ confirmSave: false });
        if (!savedProposalId) return;

        // Read again after Save. The collaboration hook resolves this from the
        // current Redux entry, so the submit cursor includes the save that just
        // completed instead of whichever envelope rendered the button.
        let submissionEnvelope = await collaboration.savePendingChanges();
        if (!submissionEnvelope) {
          throw new Error("Canonical proposal save returned no envelope before submission.");
        }
        // Between the save and the mutation on purpose: the change list — and which reviews are still
        // pending against it — are read from the envelope the save just acknowledged, so neither can be
        // described before.
        const submission = await resolveSubmission({
          envelope: submissionEnvelope,
          // The words of the button that opened it, so the leader confirms the act they chose (#5813).
          title: "Submit Changes",
          confirmButtonText: "Submit",
          context: changeDescriptionContext,
          recipient: submitRecipient,
          viewerScope,
        });
        if (submission === CANCELED_SUBMISSION) return;
        if (submission.revertSelection.length > 0) {
          const revertOperations = buildProposalSubmissionRevertOperations({
            envelope: submissionEnvelope,
            selections: submission.revertSelection,
          });
          if (revertOperations.length !== submission.revertSelection.length) {
            throw new Error("One or more proposal changes could not be reverted before submission.");
          }
          dispatch(
            proposalOperationsQueued({
              negotiationId: submissionEnvelope.negotiationId,
              operations: revertOperations,
            }),
          );
          const revertedEnvelope = await collaboration.savePendingChanges();
          if (!revertedEnvelope) {
            throw new Error("Reverted proposal changes could not be saved before submission.");
          }
          submissionEnvelope = revertedEnvelope;
        }
        // Revising a hired work agreement creates a fresh proposal draft from the immutable hired
        // snapshot. It has no submitted checkpoint in its own event stream yet, so publishing it is
        // the stream's initial Submit—not Request Revision against the completed source stream.
        // Subsequent counters remain Request Revision commands.
        const submittedEnvelope =
          submissionEnvelope.projection.status === ProposalStatus.AwaitingSubmission
            ? await submitProposalCollaborationDraft({
                negotiationId: submissionEnvelope.negotiationId,
                viewerScope,
                command: buildSubmitProposalDraftCommand({
                  envelope: submissionEnvelope,
                }),
              }).unwrap()
            : await requestProposalRevisionNegotiation({
                negotiationId: submissionEnvelope.negotiationId,
                viewerScope,
                command: buildRequestProposalRevisionNegotiationCommand({
                  envelope: submissionEnvelope,
                }),
              }).unwrap();
        dispatch(
          proposalEnvelopeReceived({
            envelope: submittedEnvelope,
            viewerScope,
          }),
        );
        // The revision is with the counterparty from here. Recorded before the navigation, so the
        // successor route this page redirects itself to renders with Submit already shut rather than
        // re-arming off the local redline it was sent from.
        setSentProposalSubmission({
          negotiationId: submittedEnvelope.negotiationId,
          contentVersion: submittedEnvelope.contentVersion,
        });
        const submittedRevisionIdValue = submittedEnvelope.projection.id;
        if (!submittedRevisionIdValue) {
          throw new Error("Submitted revision did not return an id.");
        }

        const submittedRevisionId = new Guid(submittedRevisionIdValue);
        dispatch(clearLocalProposalById(submittedRevisionId));
        if (proposal.id && !proposal.id.isEqualTo(submittedRevisionId)) {
          dispatch(clearLocalProposalById(proposal.id));
        }

        enqueueSnackbar("Requested changes sent", { variant: "success" });
        const submittedRevisionPath = `/proposals/active/revise/${submittedRevisionId.value}/details`;
        appDirectedNavigationPathRef.current = submittedRevisionPath;
        navigate(submittedRevisionPath, {
          replace: true,
        });
        return;
      }

      throw new Error("Proposal collaboration is not ready for submission.");
    } catch (error) {
      // A rejected RTK unwrap is a plain {status,data} object, so `${error}` renders "[object Object]".
      // Surface the server message when there is one, else a clear static message (never the object).
      console.error(error);
      showProposalCommandError(error, "sending your proposal changes", "proposal.submit");
    }
  }

  async function approveProposalAndHireIfAvailable(proposalToApprove: Proposal, hireIfAvailable: boolean) {
    if (!proposalToApprove.id) throw new Error("No proposal to approve");
    if (!session.user?.id) throw new Error("No user id to approve proposal");

    let approvedProposal: Proposal;
    let approvedEnvelopeForHire: ProposalCollaborationEnvelope | undefined;

    try {
      let approvedProposalJSON;
      if (collaboration.mode === "canonical") {
        const approvalEnvelope = await collaboration.savePendingChanges();
        if (!approvalEnvelope || !collaboration.viewerScope) {
          throw new Error("The canonical proposal projection is not ready for approval.");
        }
        const approvedEnvelope = await approveProposalNegotiation({
          negotiationId: approvalEnvelope.negotiationId,
          viewerScope: collaboration.viewerScope,
          command: buildApproveProposalNegotiationCommand({ envelope: approvalEnvelope }),
        }).unwrap();
        approvedEnvelopeForHire = approvedEnvelope;
        approvedProposalJSON = approvedEnvelope.projection;
      } else {
        throw new Error("Proposal collaboration is not ready for approval.");
      }
      approvedProposal =
        Proposal.fromJSON({
          json: approvedProposalJSON,
          currentUser: session.user,
          options: { withRedline: true },
        }) ?? proposalToApprove;

      if (proposalToApprove.redline?.isResolved) {
        approvedProposal = approvedProposal.updateRedline(proposalToApprove.redline);
        dispatch(updateServerProposal(getProposalJSONForServerCache(approvedProposal, approvedProposalJSON)));
      }
    } catch (error) {
      console.error(error);
      showProposalCommandError(error, "approving the proposal", "proposal.approve");
      return;
    }

    const canHireApprovedProposal =
      collaboration.mode === "canonical"
        ? Boolean(approvedEnvelopeForHire?.capabilities.canHire)
        : Boolean(session.user?.canHire(approvedProposal));
    if (hireIfAvailable && session.user?.isClient(approvedProposal) && canHireApprovedProposal) {
      try {
        let hiredProposalJSON: ProposalJSON;
        if (collaboration.mode === "canonical") {
          if (!approvedEnvelopeForHire || !collaboration.viewerScope) {
            throw new Error("The accepted canonical projection is not ready for hiring.");
          }
          const hiredEnvelope = await hireProposalNegotiation({
            negotiationId: approvedEnvelopeForHire.negotiationId,
            viewerScope: collaboration.viewerScope,
            command: buildHireProposalNegotiationCommand({ envelope: approvedEnvelopeForHire }),
          }).unwrap();
          hiredProposalJSON = hiredEnvelope.projection;
        } else {
          throw new Error("Proposal collaboration is not ready for hiring.");
        }
        enqueueSnackbar("Proposal approved and vendor team hired", {
          variant: "success",
        });
        navigateAfterHire(hiredProposalJSON);
      } catch (error) {
        console.error(error);
        showProposalCommandError(error, "hiring the vendor team", "proposal.approve-and-hire");
      }
    } else {
      enqueueSnackbar("Proposal approved", { variant: "success" });
    }
  }

  // #region Handle Approve
  async function handleApproveProposal() {
    await runProposalLifecycleActionOnce(approveProposalOnce);
  }

  async function approveProposalOnce() {
    const revision = proposal?.redlinedRevision;
    if (!revision) {
      throw new Error("No redlined revision to approve.");
    }

    if (!proposal?.id) {
      console.error("No proposal to approve");
      return;
    }
    if (!proposal.redline?.isResolved) {
      console.error("Cannot approve proposal where redline is not resolved.");
      return;
    }
    if (!session.user?.id) {
      console.error("No user id to approve proposal");
      return;
    }

    const shouldHireAfterApproval = session.user.isClient(proposal);
    const approveResponse = await confirm({
      title: shouldHireAfterApproval ? "Hire Team?" : "Approve Proposal?",
      // Approve & Hire commits the party further than Submit does, so it owes the leader the same
      // warning about their own reviewers, and on the vendor side so does Submit Approval (#6635).
      // Approving saves the staged roster first, so a reviewer staged for removal is not counted (#6869).
      // Hire, below, leaves them out too, though it hires the current envelope without saving.
      message: proposalCommitmentConfirmationMessage(
        shouldHireAfterApproval
          ? "Doing so will approve the proposal and hire the vendor team."
          : "Doing so will approve the proposal and send it to the client for final approval.",
        collaboration.envelope,
        collaboration.pendingChanges.reviewerChanges,
      ),
      okButtonText: shouldHireAfterApproval ? "Hire" : "Approve",
    });

    if (approveResponse === ConfirmResponse.Cancel) return;

    if (!proposal.isEquivalentTo(revision)) {
      try {
        let savedProposalId: string | null | undefined;
        if (collaboration.mode === "canonical") {
          savedProposalId = await handleSaveChanges({ proposal, confirmSave: false });
        } else {
          throw new Error("Proposal collaboration is not ready to accept changes.");
        }

        if (!savedProposalId) {
          showProposalCommandError(
            new Error("Accepting changes saved no proposal."),
            "accepting the changes",
            "proposal.accept-changes",
          );
          return;
        }
      } catch (error) {
        console.error(error);
        showProposalCommandError(error, "accepting the changes", "proposal.accept-changes");
        return;
      }
    }

    await approveProposalAndHireIfAvailable(proposal, shouldHireAfterApproval);
  }

  // #region Handle Accept
  async function handleAccept() {
    if (!proposal?.redline) return;

    const acceptResponse = await confirm({
      title: "Accept All Changes?",
      message: "This will mark all changes as accepted.",
      okButtonText: "Accept",
    });

    if (acceptResponse === ConfirmResponse.Cancel) return;

    let updatedRedline = proposal.redline;
    updatedRedline = updatedRedline.accept();
    handleProposalRedlineChanged(updatedRedline);
  }

  // #region Handle Reject
  async function handleReject() {
    if (!proposal?.redline) return;

    const rejectResponse = await confirm({
      title: "Reject All Changes?",
      message: "This will mark all changes as rejected.",
      okButtonText: "Reject",
    });

    if (rejectResponse === ConfirmResponse.Cancel) return;

    let updatedRedline = proposal.redline;
    updatedRedline = updatedRedline.reject();
    handleProposalRedlineChanged(updatedRedline);
  }

  // #region Handle End Negotiation
  async function handleEndNegotiation() {
    if (!proposal) return;

    try {
      const endResponse = await confirm({
        title: "End Negotiation?",
        message: "Doing so will end proposal negotiations.",
        okButtonText: "End Negotiation",
      });

      if (endResponse === ConfirmResponse.Cancel) return;

      if (collaboration.mode === "canonical") {
        if (!collaboration.envelope || !collaboration.viewerScope) {
          throw new Error("The canonical proposal projection is not ready for rejection.");
        }
        await rejectProposalNegotiation({
          negotiationId: collaboration.envelope.negotiationId,
          viewerScope: collaboration.viewerScope,
          command: buildRejectProposalNegotiationCommand({ envelope: collaboration.envelope }),
        }).unwrap();
      } else {
        throw new Error("Proposal collaboration is not ready for rejection.");
      }
      navigateToArchivedProposal();
    } catch (error) {
      console.error(error);
      showProposalCommandError(error, "rejecting the proposal", "proposal.reject");
    }
  }

  // #region Handle Cancel
  async function handleCancel() {
    if (!proposal?.id) throw new Error("No proposal to cancel.");

    try {
      const rejectResponse = await confirm({
        title: "Cancel Proposal?",
        message: "Doing so will end proposal negotiations.",
      });

      if (rejectResponse === ConfirmResponse.Cancel) return;

      if (collaboration.mode === "canonical") {
        if (!collaboration.envelope || !collaboration.viewerScope) {
          throw new Error("The canonical proposal projection is not ready for cancellation.");
        }
        await cancelProposalNegotiation({
          negotiationId: collaboration.envelope.negotiationId,
          viewerScope: collaboration.viewerScope,
          command: buildCancelProposalNegotiationCommand({ envelope: collaboration.envelope }),
        }).unwrap();
      } else {
        throw new Error("Proposal collaboration is not ready for cancellation.");
      }
      navigateToArchivedProposal();
      enqueueSnackbar("Proposal canceled", { variant: "success" });
    } catch (error) {
      console.error(error);
      showProposalCommandError(error, "canceling the proposal", "proposal.cancel");
    }
  }

  // #region Handle Discard Revision
  /**
   * Deletes a revision nobody has sent yet, and returns to what it revises (#7088).
   *
   * A revision is `AwaitingSubmission` until it is sent, and that status offers its owner Delete, not
   * Cancel. So Cancel Proposal never shows on an unsent revision, and before this the page had no way
   * to leave one except to walk away from it. Nothing was sent, so nothing is archived: the work
   * agreement goes on under the terms it was hired on.
   */
  async function handleDiscardRevision(revision: DiscardableRevision) {
    const discardResponse = await confirm({
      title: "Discard Revision?",
      message: "The changes in this revision have not been sent, and they will be deleted. This cannot be undone.",
      okButtonText: "Discard Revision",
    });
    if (discardResponse === ConfirmResponse.Cancel) return;

    try {
      isDiscardingRevisionRef.current = true;
      setIsDiscardingRevision(true);
      await deleteProposal({ proposalId: revision.id.value }).unwrap();
    } catch (error) {
      isDiscardingRevisionRef.current = false;
      setIsDiscardingRevision(false);
      console.error(error);
      showProposalCommandError(error, "discarding the revision", "proposal.delete");
      return;
    }

    dispatch(clearUnsavedCommentsByProposalId(revision.id));
    collaboration.discardPendingChanges();
    enqueueSnackbar("Revision discarded", { variant: "success" });
    const workAgreementPath = `/work/active/view/${revision.workAgreementId.value}/details`;
    appDirectedNavigationPathRef.current = workAgreementPath;
    navigate(workAgreementPath, { replace: true });
  }

  // #region Handle Hire
  async function handleHire() {
    await runProposalLifecycleActionOnce(hireOnce);
  }

  async function hireOnce() {
    if (!proposal?.id) throw new Error("No proposal to hire.");

    try {
      // The shared count and sentence, not a reviewer loop of its own: Hire used to word and count this
      // differently from Submit and from the header's badge (#6635). A reviewer the leader has staged for
      // removal is not counted: the leader has already decided their review does not count (#6869).
      const hireResponse = await confirm({
        title: "Hire?",
        message: proposalCommitmentConfirmationMessage(
          "Hire the selected vendor team?",
          collaboration.envelope,
          collaboration.pendingChanges.reviewerChanges,
        ),
        okButtonText: "Hire",
      });

      if (hireResponse === ConfirmResponse.Cancel) return;

      let hiredProposalJSON: ProposalJSON;
      if (collaboration.mode === "canonical") {
        if (!collaboration.envelope || !collaboration.viewerScope) {
          throw new Error("The canonical proposal projection is not ready for hiring.");
        }
        const hiredEnvelope = await hireProposalNegotiation({
          negotiationId: collaboration.envelope.negotiationId,
          viewerScope: collaboration.viewerScope,
          command: buildHireProposalNegotiationCommand({ envelope: collaboration.envelope }),
        }).unwrap();
        hiredProposalJSON = hiredEnvelope.projection;
      } else {
        throw new Error("Proposal collaboration is not ready for hiring.");
      }

      enqueueSnackbar("Proposal hired", { variant: "success" });

      navigateAfterHire(hiredProposalJSON);
    } catch (error) {
      console.error(error);
      showProposalCommandError(error, "hiring the vendor team", "proposal.hire");
    }
  }

  function applyPendingUnsavedProposalUpdate(updatedProposal: Proposal) {
    if (collaboration.mode === "canonical") {
      collaboration.queueProposalUpdate(updatedProposal, { flattenRedline: true });
      return;
    }

    const pendingUnsavedProposal = Proposal.getLocalVersionForUpdate({
      savedProposal,
      updatedProposal,
    });

    if (pendingUnsavedProposal) {
      dispatch(updateLocalProposal(pendingUnsavedProposal));
      return;
    }

    if (updatedProposal.id) {
      dispatch(updateLocalProposal(updatedProposal));
    }
  }

  // #region Handle Team Leader Reverted
  // The vendor-team leader is not modeled on `ProposalRedline` (it is a scalar `initialSetupOnly` field,
  // unlike the roster it sits beside — see proposal-command-builder.ts), so a revert cannot flow through
  // `handleProposalRedlineChanged`. Instead it goes through the leader's own setter
  // (`Proposal.updateVendorTeamLeader`, mirroring `updateVendorFeeScheduleTemplateIds` etc.) straight to
  // `applyPendingUnsavedProposalUpdate`, so `queueProposalUpdate`'s flatten-and-diff still builds the
  // real `/vendorTeam/leader` FieldSet fresh — never a raw whole-team overwrite of `members`.
  function handleTeamLeaderReverted(leader: EntityMember) {
    const currentProposal = proposal;
    if (!currentProposal) {
      console.error("No proposal to revert vendor team leader");
      return;
    }
    try {
      applyPendingUnsavedProposalUpdate(currentProposal.updateVendorTeamLeader(leader));
    } catch (error) {
      console.error("Error reverting vendor team leader", error);
      showErrorSnackbar(error, { doing: "reverting the vendor team leader", operation: "proposal.revert-vendor-team-leader" });
    }
  }

  // #region Handle Undo
  async function handleUndoChanges() {
    if (!proposal?.redline?.canBeUndone) return;

    const undoResponse = await confirm({
      title: "Undo All?",
      message: "Doing so will revert all changes.",
      okButtonText: "Undo All",
    });

    if (undoResponse === ConfirmResponse.Cancel) return;

    // Undo every field through the redline's own undo so each one records an explicit Undo entry in
    // its session history. That entry is what the canonical resolution-decision builder reads to
    // revert a decided target back to Unresolved, and what the flatten-and-diff reads to revert a
    // plain local edit. Swapping the last-saved redline in wholesale — the legacy `undoLocalChanges`
    // shortcut this replaced — carries no session history, so it queued no decisions at all and the
    // button did nothing.
    handleProposalRedlineChanged(proposal.redline.undo());
  }

  // #region Handle Redline Changed
  function handleProposalRedlineChanged(
    newProposalRedline: ProposalRedlineUpdate,
    traversalFieldOverride?: ProposalField | null,
  ) {
    const currentProposal = proposal;
    if (reviewCompletionLocksEditing) return;
    if (currentProposal?.isArchived) return;
    if (!currentProposal?.redline) {
      throw new Error("Current proposal redline is missing");
    }

    const resolvedRedline =
      typeof newProposalRedline === "function" ? newProposalRedline(currentProposal.redline) : newProposalRedline;
    // `restorePersistedFieldAfterLocalUndo` is a legacy-model helper: when an undo
    // reverts a field back to the persisted value it swaps in the persisted field
    // redline, which discards the undo's session-history entry. In canonical mode
    // that entry is exactly what the resolution-decision builder needs to revert
    // the target to Unresolved, and `queueProposalUpdate` already diffs the raw
    // redline correctly, so the raw undone redline must flow through untouched.
    const redlineToUpdate =
      collaboration.mode !== "canonical" &&
      typeof currentProposal.redline.restorePersistedFieldAfterLocalUndo === "function"
        ? currentProposal.redline.restorePersistedFieldAfterLocalUndo({
            persistedRedline: savedProposal?.redline ?? currentProposal.persistedRedline,
            traversalFieldOverride,
            updatedRedline: resolvedRedline,
          })
        : resolvedRedline;
    const updatedProposal = currentProposal.updateRedline(redlineToUpdate);
    applyPendingUnsavedProposalUpdate(updatedProposal);
  }

  // #region Handle Template Selection Updated
  // Which templates are ticked belongs to the proposal, not to the tab that draws the boxes and not
  // to the redline: the tab is unmounted whenever another one is shown, and the redline is rebuilt
  // from the effective proposal on every render. Each tab reads its selection back off the proposal
  // (`currentParty*TemplateIds`) and hands a change here, so the ids travel in the same update as the
  // entries the template added or took away. The proposal is the one the tab was drawn from, passed in
  // where the tab is rendered: a tab is only ever drawn with one.
  function handleTeamTemplatesUpdated(
    currentProposal: Proposal,
    templateIds: Guid[],
    newProposalRedline?: ProposalRedlineUpdate,
  ) {
    applyTemplateSelection(
      currentProposal,
      (selecting) => selecting.updateCurrentPartyTeamTemplateIds(templateIds),
      newProposalRedline,
    );
  }

  function handleFeeScheduleTemplatesUpdated(
    currentProposal: Proposal,
    templateIds: Guid[],
    newProposalRedline?: ProposalRedlineUpdate,
  ) {
    applyTemplateSelection(
      currentProposal,
      (selecting) => selecting.updateCurrentPartyFeeScheduleTemplateIds(templateIds),
      newProposalRedline,
    );
  }

  function applyTemplateSelection(
    currentProposal: Proposal,
    updateTemplateIds: (selecting: Proposal) => Proposal,
    newProposalRedline?: ProposalRedlineUpdate,
  ) {
    // A template's entries are a redline edit, so an archived proposal refuses them as
    // `handleProposalRedlineChanged` does. Its other lock, `reviewCompletionLocksEditing`, holds only
    // outside canonical mode, where the page has no proposal to hand a tab in the first place.
    if (currentProposal.isArchived) return;
    let updatedProposal = updateTemplateIds(currentProposal);
    if (newProposalRedline) {
      const resolvedRedline =
        typeof newProposalRedline === "function" && updatedProposal.redline
          ? newProposalRedline(updatedProposal.redline)
          : newProposalRedline;

      if (resolvedRedline instanceof ProposalRedline) {
        updatedProposal = updatedProposal.updateRedline(resolvedRedline);
      }
    }
    applyPendingUnsavedProposalUpdate(updatedProposal);
  }

  function updateCurrentProposalReviewers(updatedReviewers: ProposalReviewer[]): Proposal | null {
    const viewingAsVendor = session.context?.viewingAsVendor ?? false;
    const currentProposal = proposal;
    const proposalToUpdate = getReviewerUpdateSourceProposal({
      fallbackProposal: savedProposal,
      proposal: currentProposal,
      viewingAsVendor,
    });

    if (!proposalToUpdate) return null;

    return viewingAsVendor
      ? proposalToUpdate.updateVendorReviewers(updatedReviewers)
      : proposalToUpdate.updateClientReviewers(updatedReviewers);
  }

  // #region Handle Save
  const handleSaveChanges = useCallback(
    async (args?: {
      proposal?: Proposal;
      confirmSave?: boolean;
      confirmationMode?: ConfirmationMode;
    }): Promise<string | null | undefined> => {
      const {
        proposal: proposalToSave = proposal,
        confirmSave,
        confirmationMode = "review",
      } = args ?? {};
      const saveTargetProposalId = proposalToSave?.id?.value;

      setIsSaving(true);

      try {
        if (!proposalToSave) {
          const missing = new Error("No proposal to save");
          console.error(missing);
          showErrorSnackbar(missing, { doing: "saving your proposal changes", operation: "proposal.save" });
          return;
        }
        const {
          hasActualSaveUpdates,
          proposalHasUnsavedRedlineChanges,
          proposalIsModified,
          proposalReviewersAreModified,
        } = Proposal.getSaveState({
          includeExplicitReviewerUpdates: true,
          proposal: proposalToSave,
          savedProposal,
        });
        const reviewersToSave = session.context?.viewingAsVendor
          ? proposalToSave.vendorReviewers
          : proposalToSave.clientReviewers;
        const baselineProposalToSave = savedProposal ?? proposalToSave;
        const proposalSavePatchPayload = getProposalSavePatchPayload({
          originalProposal: baselineProposalToSave,
          updatedProposal: proposalToSave,
          session,
        });
        let proposalHasPatchableSaveChanges =
          collaboration.mode === "canonical"
            ? collaboration.hasPendingChanges
            : proposalSavePatchPayload === null
              ? proposalIsModified
              : proposalSavePatchPayload.length > 0;
        const originalReviewersToSave =
          (session.context?.viewingAsVendor
            ? baselineProposalToSave.vendorReviewers
            : baselineProposalToSave.clientReviewers) ?? [];
        const reviewerActionStateToSave = getReviewerActionState({
          originalReviewers: originalReviewersToSave,
          selectedReviewers: reviewersToSave,
          proposalIsModifiedByBuilder: false,
          proposalHasUnsavedRedlineChanges,
          reviewersAreModified: proposalReviewersAreModified,
        });
        const hasReviewerContext = reviewersToSave.length > 0 || reviewerActionStateToSave.hasReviewerChanges;

        if (confirmSave) {
          // Same share plan the draft editor shows: the queued operations and reviewer changes,
          // described in the change-history vocabulary, with the audience named.
          const sharePlan = buildProposalSaveSharePlan({
            operations: collaboration.pendingChanges.operations,
            reviewerChanges: collaboration.pendingChanges.reviewerChanges,
            audienceLabel: proposalSaveAudienceLabel({
              reviewerCount: reviewersToSave.length,
              isReviewing,
            }),
            context: changeDescriptionContext,
          });
          const confirmationCopy = getSaveConfirmationCopy({
            confirmationMode,
            hasPendingChanges: proposalHasUnsavedRedlineChanges,
            hasReviewerChanges: reviewerActionStateToSave.hasReviewerChanges,
            hasReviewerContext,
            hasReviewerRecipients: reviewersToSave.length > 0,
            proposalIsSaved: Boolean(proposalToSave.id),
            reviewerConfirmMessage: reviewerActionStateToSave.confirmMessage,
            reviewerConfirmOkButtonText: reviewerActionStateToSave.confirmOkButtonText,
            reviewerConfirmSupplementalMessage: reviewerActionStateToSave.confirmSupplementalMessage,
            reviewerConfirmTitle: reviewerActionStateToSave.confirmTitle,
            audienceLabel: sharePlan.audienceLabel,
            // The dialog renders this list below the message, so the message stops summarising it.
            describedChangeCount: sharePlan.contentChangeCount,
            describedReviewerChangeCount: sharePlan.reviewerChangeCount,
          });
          const response = await confirm({
            title: confirmationCopy.title,
            // The change list renders rather than flattening into the message string, so it keeps its
            // bullets and shows each value transition as a redline instead of plain grey text. Suppressed
            // entirely on a draft's first-ever save (#4952): there is no prior version to diff against,
            // so the list would only repeat what the actor just typed.
            message: (
              <>
                {confirmationCopy.message}
                {confirmationCopy.showsChangeSummary && (
                  <ProposalChangeSummaryList
                    heading={confirmationCopy.changeLogHeading}
                    descriptors={sharePlan.descriptors}
                  />
                )}
              </>
            ),
            okButtonText: confirmationCopy.okButtonText,
            // Deliberately no comment option. A save used to offer "Save and publish pending internal
            // comments", because a comment sat in the browser until one did; comments are written as
            // they are made, so a save has nothing of the conversation to offer.
          });

          if (response === ConfirmResponse.Cancel) return;
        }

        dispatchPanelState({ type: "closeReviewerSelector" });

        const canPersistProposalUpdates = collaboration.canPersistProposalUpdates(proposalToSave);

        if (hasActualSaveUpdates && proposalHasPatchableSaveChanges && !canPersistProposalUpdates) {
          enqueueSnackbar("You do not have permission to save updates to this proposal", {
            variant: "warning",
          });
          return;
        }

        if (collaboration.mode === "canonical") {
          // Queue from the exact object passed by the caller before reading the
          // command. The hook reads Redux synchronously, so a blur immediately
          // followed by Save cannot drop the final edit.
          // Only an actor permitted to persist proposal-content/reviewer changes queues and flushes
          // them, matching the draft page. Without this, a comment-only reviewer fell through the gate
          // above whenever `hasActualSaveUpdates` was false and still re-diffed the editor into a save
          // the server rejects with a bare 403 (#4535).
          const queuedUpdate = canPersistProposalUpdates
            ? collaboration.queueProposalUpdate(proposalToSave, { flattenRedline: true })
            : false;
          // The staged client team roster rides this same press without being proposal content, and
          // managing it is its own capability: a Client Representative who may not edit the draft
          // must still reach the save that writes their roster (#5116).
          proposalHasPatchableSaveChanges =
            (canPersistProposalUpdates && (proposalHasPatchableSaveChanges || queuedUpdate)) ||
            collaboration.hasPendingClientTeamMembers;

          let savedEnvelope = collaboration.envelope;
          if (proposalHasPatchableSaveChanges) {
            savedEnvelope = await collaboration.savePendingChanges();
            if (!savedEnvelope) throw new Error("Canonical proposal save returned no envelope.");

            // The canonical envelope and operation queue now own this editor.
            // Remove any legacy local override only after the command has an
            // authoritative response, so stale proposal cache data cannot be
            // reintroduced by another selector or a later route transition.
            const savedProposalId = Guid.fromJSON(savedEnvelope.projection.id ?? saveTargetProposalId);
            if (savedProposalId) dispatch(clearLocalProposalById(savedProposalId));
          }

          if (proposalHasPatchableSaveChanges) {
            enqueueSnackbar(
              getProposalSaveSuccessMessage({
                confirmationMode,
                hasReviewerOnlyUpdates: hasReviewerContext && !reviewerActionStateToSave.hasPendingSaveUpdates,
              }),
              { variant: "success" },
            );

            // S2 (ROADMAP.md): a save is never rejected for a content conflict, so the 200 above can
            // still have left part of what was just saved un-persisted. Surface it — a silent partial
            // success must never look identical to a full one.
            const dropWarning = getProposalOperationDropWarning(savedEnvelope?.operationReceipts);
            if (dropWarning) enqueueSnackbar(dropWarning, { variant: "warning", persist: true });
          }

          return savedEnvelope?.projection.id ?? proposalToSave.id?.value;
        }

        throw new Error("Proposal collaboration is not ready to save.");
      } catch (error) {
        console.error("Failed to save proposal", error);
        if (reportProposalMissingItemSaveFailure(error, collaboration)) return;
        showProposalCommandError(error, "saving your proposal changes", "proposal.save");
      } finally {
        setIsSaving(false);
      }
    },
    [
      changeDescriptionContext,
      isReviewing,
      confirm,
      collaboration,
      dispatch,
      dispatchPanelState,
      proposal,
      savedProposal,
      session,
    ],
  );

  // #region Handle Close Comments Drawer
  function handleCloseCommentsDrawer() {
    dispatchPanelState({ type: "closeComments" });
  }

  // #region Handle Open Comments Drawer
  /** Opening the pane marks nothing read (#5824): the comments the reader scrolls into view are. */
  function handleOpenCommentsDrawer(field?: ProposalField, title?: string) {
    dispatchPanelState({ type: "openComments", field, title });
  }

  // A link asked for the Comments pane: `?pane=comments`, and the conversation with `&forum=` (#5826).
  // It opens once the pane has a proposal to show. Each request opens it once, so the reader can close
  // it afterwards. Like any other opening it marks nothing read (#5824): the comments the reader then
  // scrolls into view are.
  const { requestKey: commentsPaneRequestKey, forumRequest: commentsForumRequest } =
    useConversationPaneRequest(ConversationPane.Comments);
  const openedCommentsPaneRequestKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!commentsPaneRequestKey || !commentProposal?.id) return;
    if (openedCommentsPaneRequestKeyRef.current === commentsPaneRequestKey) return;
    openedCommentsPaneRequestKeyRef.current = commentsPaneRequestKey;
    dispatchPanelState({ type: "openComments" });
  }, [commentProposal?.id, commentsPaneRequestKey, dispatchPanelState]);

  function navigateAfterReviewFinalized() {
    // The guard compares pathnames, and the stage chip is in the query.
    appDirectedNavigationPathRef.current = "/proposals";
    navigate(proposalListPath(ProposalStageChip.Active), { replace: true });
  }

  async function completeCurrentReview() {
    if (collaboration.mode === "canonical") {
      // This is deliberately called even when the queue looks empty. The hook
      // reads Redux at invocation time and returns the newest acknowledged
      // envelope, including a save that completed immediately before this call.
      const freshEnvelope = await collaboration.savePendingChanges();
      if (!freshEnvelope || !collaboration.viewerScope) {
        throw new Error("Canonical proposal review is missing its authoritative envelope.");
      }
      await completeProposalReview({
        negotiationId: freshEnvelope.negotiationId,
        viewerScope: collaboration.viewerScope,
        command: buildCompleteProposalReviewCommand({ envelope: freshEnvelope }),
      }).unwrap();
      return;
    }

    throw new Error("Proposal collaboration is not ready to complete a review.");
  }

  // #region Handle Finalize Review
  async function handleFinalizeReviewClicked() {
    if (!proposal?.id) throw new Error("No proposal to finalize review for");

    try {
      // The review button routes pending local work through
      // handleSubmitAllAndFinalizeReviewClicked. A plain "Complete Review" is a
      // versioned endorsement of the freshest canonical envelope, not another
      // opportunity to re-diff the editor and fabricate a hidden save.
      await completeCurrentReview();
      enqueueSnackbar("Proposal review finalized", { variant: "success" });
      navigateAfterReviewFinalized();
    } catch (error) {
      console.error(error);
      showProposalCommandError(error, "finalizing your review", "proposal.finalize-review");
    }
  }

  async function handleSubmitAllAndFinalizeReviewClicked() {
    if (!proposal?.id) throw new Error("No proposal to finalize review for");

    try {
      const { proposalHasUnsavedRedlineChanges: legacyProposalHasUnsavedRedlineChanges } = Proposal.getSaveState({
        proposal,
        savedProposal,
      });
      const currentProposalHasUnsavedRedlineChanges =
        collaboration.mode === "canonical" ? collaboration.hasPendingChanges : legacyProposalHasUnsavedRedlineChanges;

      // Only the reviewer's proposal-content work. This used to also run for pending comments, because
      // completing a review without a save left the reviewer's rationale sitting unsent in the browser;
      // a comment reaches its audience when it is written now, so finishing a review has nothing of the
      // conversation left to flush.
      // `handleSaveChanges` refuses to persist for an actor who may not, so asking it to save
      // unsendable work would only fail the completion. A comment-only reviewer can still be holding
      // queued content — a durable local queue built while they had edit rights that were later
      // revoked — and that work is unsendable by definition: the server rejects a save carrying any
      // operation from an actor without `canEditDraft` (#4535). Skip the save and endorse the current
      // version; the local work stays queued rather than being destroyed.
      if (currentProposalHasUnsavedRedlineChanges && collaboration.canPersistProposalUpdates(proposal)) {
        const savedProposalId = await handleSaveChanges({
          confirmSave: false,
        });

        if (!savedProposalId) {
          throw new Error("Failed to save proposal updates before finalizing review.");
        }
      }

      await completeCurrentReview();
      enqueueSnackbar("Proposal review finalized", { variant: "success" });
      navigateAfterReviewFinalized();
    } catch (error) {
      console.error(error);
      showProposalCommandError(error, "finalizing your review", "proposal.finalize-review");
    }
  }

  // #region Handle Team Member Quit
  async function handleTeamMemberQuitProposal(memberId: Guid): Promise<void> {
    if (!proposal?.workAgreementId || !memberId.isEqualTo(session.user?.id)) {
      throw new Error("No work agreement or invalid member to leave the proposal team.");
    }
    const workAgreementVersion = proposal.workAgreement.version;
    if (!workAgreementVersion) {
      throw new Error("No work agreement version to leave the proposal team.");
    }

    try {
      await quitProposalTeamAPICommand({
        workAgreementId: proposal.workAgreementId.value,
        version: workAgreementVersion,
      }).unwrap();
      enqueueSnackbar("You have left the proposal's vendor team", {
        variant: "success",
      });
    } catch (error) {
      console.error(error);
      showErrorSnackbar(error, { doing: "leaving the proposal's vendor team", operation: "proposal.leave-vendor-team" });
    }
  }

  // #region Handle Navigate Back
  async function handleNavigateBack(continueNavigation: () => void) {
    if (isDirty) {
      const response = await confirm({
        title: "Discard changes?",
        message: "Are you sure you want to discard changes to the proposal?",
        okButtonText: "Discard",
        alternativeOkText: disableSaveChanges ? undefined : "Save & Close",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return false;
      if (response === ConfirmResponse.AltOk) {
        const savedProposalId = await handleSaveChanges({
          confirmSave: false,
        });
        if (!savedProposalId) return false;
      }
    }

    dispatch(clearUnsavedCommentsByProposalId(proposalId));
    if (collaboration.mode === "canonical") {
      collaboration.discardPendingChanges();
    } else if (savedProposal) {
      dispatch(updateLocalProposal(savedProposal));
    }

    continueNavigation();
    return true;
  }

  // The guard owns the blocked navigation from here: it asks `handleNavigateBack` what to do, and it
  // takes the user to the route they clicked even when the page navigates while the prompt is open. A
  // save on this page does not move the URL itself, but the successor-checkpoint effect
  // above does when the other party advances the negotiation while the prompt is open (#5545).
  // Opening another proposal in this editor leaves this one's edits behind, so it asks too (#7070).
  useUnsavedProposalNavigationGuard({
    shouldBlockNavigation: ({ currentLocation, nextLocation }) =>
      isDirty &&
      navigationLeavesProposalEditor("active", currentLocation.pathname, nextLocation.pathname),
    onNavigateAway: handleNavigateBack,
    appDirectedNavigationPathRef,
  });
  // Reloading, closing the tab and typing an address never reach the router (#7070).
  useAskBeforeUnload(isDirty);

  /**
   * The marketplace team behind this proposal's vendor team, when there is one.
   *
   * Read once here rather than in the drawer's props, so the fee-schedule panel and anything else
   * that needs it cannot disagree about which team's profile is being offered.
   */
  const marketplaceVendorTeamId = getMarketplaceTeamId(proposal?.vendorTeam);

  // #region Handle Create Category
  function handleCreateCategory() {
    dispatchPanelState({ type: "openAddFeePanel" });
  }

  /** Opens the category editor on a fee schedule row, filled from the row as it stands (#5396). */
  function handleEditCategory(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) {
    dispatchPanelState({ type: "openAddFeePanel", category });
  }

  function handleAddExistingCategory() {
    dispatchPanelState({ type: "openExistingFeePanel" });
  }

  /**
   * A press on an issue, in a tab's list or in its badge's popover (#6097). It moves to the field, as
   * "Prev / Next Issue" would, and scrolls the grid to the cell the issue is about. An issue about a
   * rate's Team Member or Fee also opens that category's editor on that field, which is where the
   * reader fixes it — the same editor the row's Edit action opens, read-only when the proposal cannot
   * be changed right now.
   */
  function handleIssueSelected(issue: ProposalIssue) {
    handleTraverseToNewField(issue.field);
    revealFeeScheduleIssueCell(issue);
    const focus = issue.categoryEditorField;
    if (!focus || !issue.field.id) return;
    const feeScheduleRedline = proposal?.redline?.feeSchedule;
    if (!Masked.isPresentNotMasked(feeScheduleRedline)) return;
    const categoryRedline = feeScheduleRedline.redlines.find((redline) => redline.field.id?.isEqualTo(issue.field.id));
    const category = categoryRedline?.isRemoved ? null : (categoryRedline?.currentEntry ?? categoryRedline?.revisedEntry);
    if (!category) return;
    dispatchPanelState({ type: "openAddFeePanel", category, focus });
  }

  /**
   * Asks about each picked category whose name the fee schedule already has, and says what to do with
   * each. Only the answers are kept: the change itself is made later, on the redline as it then
   * stands (see `withExistingCategoryMerges`).
   */
  async function chooseExistingCategoryMerges(args: {
    categories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>;
    existingCategories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>;
    feeScheduleRedline: FeeScheduleRedline;
  }): Promise<ExistingCategoryMerge[]> {
    const { categories, existingCategories, feeScheduleRedline } = args;
    const merges: ExistingCategoryMerge[] = [];

    for (const category of categories) {
      if (existingCategories.some((c) => c.isEqualTo(category))) continue;

      if (!feeScheduleRedline.getMatchingCategoryRedline(category)) {
        merges.push({ category, overwrite: false });
        continue;
      }

      const response = await confirm({
        title: "Overwrite Category?",
        message: `A category with the name "${category.name}" already exists. Do you want to overwrite it?`,
        okButtonText: "Overwrite",
      });

      if (response === ConfirmResponse.Cancel) continue;

      merges.push({ category, overwrite: true });
    }

    return merges;
  }

  function validateCategoryCanBeAdded(args: {
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
    feeScheduleRedline: FeeScheduleRedline | Masked | undefined;
    selectedCategories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> | Masked;
  }): FeeScheduleRedline | null {
    const { category, feeScheduleRedline, selectedCategories } = args;

    if (!feeScheduleRedline) {
      throw new Error("Proposal redline is missing fee schedule redline.");
    }
    if (Masked.isMasked(selectedCategories) || Masked.isMasked(category)) {
      enqueueSnackbar("Cannot add category because the fee schedule is masked", { variant: "warning" });
      return null;
    }
    if (Masked.isMasked(feeScheduleRedline)) {
      enqueueSnackbar("Cannot modify fee schedule when it is masked.", {
        variant: "warning",
      });
      return null;
    }
    if (selectedCategories.some((c) => c.isEqualTo(category))) {
      enqueueSnackbar("Fee category is already in the fee schedule", {
        variant: "info",
      });
      return null;
    }

    return feeScheduleRedline;
  }

  // #region Handle Existing Fee Categories Selected
  async function handleExistingCategoriesSelected(
    categories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>,
  ) {
    if (!proposal) {
      throw new Error("Proposal is required to add fee categories.");
    }

    const feeScheduleRedline = proposal.redline?.feeSchedule;
    const existingCategories = proposal.workAgreement?.feeSchedule ?? [];

    if (!feeScheduleRedline) {
      throw new Error("Proposal redline is missing fee schedule redline.");
    }
    if (Masked.isMasked(existingCategories) || Masked.isMasked(categories)) {
      showActionableErrorSnackbar(HIDDEN_RATES_ADD_MESSAGE);
      dispatchPanelState({ type: "closeExistingFeePanel" });
      return;
    }
    if (Masked.isMasked(feeScheduleRedline)) {
      enqueueSnackbar("Cannot modify fee schedule when it is masked.", {
        variant: "warning",
      });
      return;
    }

    // A team's published rate names who bills it (#4920); a catalog category names nobody, so this
    // asks nothing for those. Anybody who cannot bill this proposal is put to the user, and adding
    // them is a redline of the team the counterparty sees, as a template's people are (#5237).
    const teamRoster = getFeeScheduleTeamRosterFromRedline(proposal.vendorTeam, proposal.redline, proposal.clientTeam);
    const staffing = await reconcileStaffing({
      categories,
      teamMembers: getFeeScheduleTeamMembers(proposal.vendorTeam),
      teamIsMasked: Masked.isMasked(proposal.redline?.vendorTeam),
      excludedLegalEntityId: teamRoster.excludedLegalEntityId,
    });
    if (!staffing.applied) return;

    const merges = await chooseExistingCategoryMerges({
      categories: staffing.categories,
      existingCategories,
      feeScheduleRedline,
    });

    try {
      // Both questions wait on the user, and a collaborator's save can land meanwhile. The change is
      // made on the redline as it stands now, through the latest render's handler, or the proposal
      // it queues would carry that save back out as reversions (#4920).
      handleProposalRedlineChangedRef.current((currentRedline) => {
        if (Masked.isMasked(currentRedline.feeSchedule)) return currentRedline;
        const { feeScheduleRedline: mergedFeeSchedule, mergedCategories } = withExistingCategoryMerges(
          currentRedline.feeSchedule,
          merges,
        );
        // Only the people billing a rate that went on: a skipped rate seats nobody.
        return withStaffingSeatedOnTeamRedline(
          currentRedline.updateFeeScheduleRedline(mergedFeeSchedule),
          staffingForRates(staffing.membersToAddToTeam, mergedCategories),
        );
      });
    } catch (error) {
      console.error(error);
      showErrorSnackbar(error, { doing: "updating the fee schedule", operation: "proposal.fee-schedule.update" });
    }

    dispatchPanelState({ type: "closeExistingFeePanel" });
  }

  // #region Handle Fee Category Added
  async function handleCategoryAdded(addedCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) {
    if (!proposal) {
      throw new Error("Proposal is required to add a fee category.");
    }

    if (!proposal.redline) {
      throw new Error("Proposal redline is required to add a fee category.");
    }

    const selectedCategories = proposal.workAgreement?.feeSchedule ?? [];
    const feeScheduleRedline = validateCategoryCanBeAdded({
      category: addedCategory,
      feeScheduleRedline: proposal.redline.feeSchedule,
      selectedCategories,
    });

    if (!feeScheduleRedline) return;

    // The field offers the vendor team first, and reaches anybody else through "Browse for new
    // member", which means "add them to the team and to this rate" (#5798). So a newcomer is
    // proposed for the team in the same change — a team redline the counterparty sees and may
    // reject — because the proposal may bill only its vendor team (`WorkAgreement.ValidateFeeSchedule`).
    const category = addedCategory;
    const newcomer = getFeeScheduleTeamRosterFromRedline(proposal.vendorTeam, proposal.redline).newcomerNamedBy(
      category.teamMember,
      null,
    );
    const membersToSeat = newcomer ? [newcomer] : [];

    let newRedline: FeeScheduleRedline;

    const matchingRedline = feeScheduleRedline.getMatchingCategoryRedline(category);
    if (matchingRedline) {
      const response = await confirm({
        title: "Overwrite Category?",
        message: "A category with the same name already exists. Do you want to overwrite it?",
        okButtonText: "Overwrite",
      });

      if (response === ConfirmResponse.Cancel) return;

      newRedline = feeScheduleRedline.replaceEntryById(category.id, category);
    } else {
      newRedline = feeScheduleRedline.addEntry(category);
    }

    let categoryWasAdded = false;
    try {
      handleProposalRedlineChanged((currentRedline) =>
        withStaffingSeatedOnTeamRedline(currentRedline.updateFeeScheduleRedline(newRedline), membersToSeat),
      );
      categoryWasAdded = true;
    } catch (error) {
      console.error(error);
      showErrorSnackbar(error, { doing: "updating the fee schedule", operation: "proposal.fee-schedule.update" });
    }

    if (categoryWasAdded) {
      setNewFeeCategoryFormKey(Guid.generate().value);
    }
    dispatchPanelState({ type: "closeAddFeePanel", clearCategory: true });
  }

  // #region Handle Fee Category Updated
  /**
   * Records a save from the category editor that a row's Edit action opened (#5396).
   *
   * `reviseEntryById` compares the save with the category the editor opened on, and redlines only the
   * columns that differ. So the save sends what the row's inline cell editors would have sent, and a
   * column the user did not touch keeps whatever the row holds now.
   */
  function handleCategoryUpdated(savedCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) {
    const openedCategory = currentCategory;
    const feeScheduleRedline = proposal?.redline?.feeSchedule;
    if (!openedCategory || !feeScheduleRedline) return;
    if (Masked.isMasked(feeScheduleRedline)) {
      enqueueSnackbar("Cannot modify fee schedule when it is masked.", { variant: "warning" });
      return;
    }

    // Somebody chosen through "Browse for new member" joins the team with the rate, as on an added
    // rate. A rate that already named somebody off the team and keeps them adds nobody (#5798).
    const newcomer = getFeeScheduleTeamRosterFromRedline(proposal?.vendorTeam, proposal?.redline).newcomerNamedBy(
      savedCategory.teamMember,
      openedCategory.teamMember,
    );
    const membersToSeat = newcomer ? [newcomer] : [];

    let categoryWasUpdated = false;
    try {
      const newRedline = feeScheduleRedline.reviseEntryById(openedCategory.id, openedCategory, savedCategory);
      handleProposalRedlineChanged((currentRedline) =>
        withStaffingSeatedOnTeamRedline(currentRedline.updateFeeScheduleRedline(newRedline), membersToSeat),
      );
      categoryWasUpdated = true;
    } catch (error) {
      console.error(error);
      showErrorSnackbar(error, { doing: "updating the fee schedule", operation: "proposal.fee-schedule.update" });
    }

    if (categoryWasUpdated) {
      dispatchPanelState({ type: "closeAddFeePanel", clearCategory: true });
    }
  }

  // The row the editor was opened on. With it the panel draws every field with its redline, as the
  // grid does, and it is the only place a category's fields are edited on this page (#5570).
  const openedFeeScheduleRedline = proposal?.redline?.feeSchedule;
  const currentCategoryRedline =
    currentCategory && Masked.isPresentNotMasked(openedFeeScheduleRedline)
      ? openedFeeScheduleRedline.redlines.find((redline) => redline.field.id?.isEqualTo(currentCategory.id))
      : undefined;

  // Who leads either team, while the proposal is negotiated (#5836).
  const leaderHandover = useProposalLeaderHandover(proposal ?? undefined);

  // #region Render
  if (!sessionPermissions) return permissionsLoadState;

  function navigateAfterHire(hiredProposalJSON: ProposalJSON) {
    if (isOnboardingProposal) {
      appDirectedNavigationPathRef.current = "/dashboard";
      return;
    }

    const hiredWorkAgreementId = hiredProposalJSON.workAgreementId;
    if (!hiredWorkAgreementId) return;

    const hiredWorkAgreementPath = `/work/active/view/${hiredWorkAgreementId}/details`;
    appDirectedNavigationPathRef.current = hiredWorkAgreementPath;
    navigate(hiredWorkAgreementPath, { replace: true });
  }

  const hasAuthorizedCanonicalProjection = collaboration.mode === "canonical" && Boolean(collaboration.envelope);

  function handleRetryProposalLoad() {
    // A rejected retry leaves the page in exactly the state it is already in, so there is nothing to
    // surface beyond the console: the failure view is still on screen with the button ready again.
    void collaboration.refetch().catch((error: unknown) => {
      console.error("Failed to reload the proposal", error);
    });
  }

  if (!sessionPermissions.includesAccessToProposals && collaboration.mode === "loading") {
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  }
  if (!sessionPermissions.includesAccessToProposals && !hasAuthorizedCanonicalProjection) {
    return <NotAuthorized />;
  }
  if (collaboration.mode === "error") {
    // #4848. This used to render the same loader the page shows while loading, which made a failed
    // (or timed-out) negotiation load indistinguishable from a slow one and left the viewer on a
    // spinner that would never stop. A terminal "gone" answer never reaches here — the lifecycle
    // effect above ejects the viewer for those — so everything that does is worth retrying.
    //
    // The submit dialog renders alongside it on purpose: `resolveSubmission` awaits a promise that
    // only the dialog can settle, so an early return that dropped the dialog left an in-flight
    // submission's button spinning forever as well, whatever the page said around it.
    return (
      <>
        <ProposalLoadFailure onRetry={handleRetryProposalLoad} isRetrying={collaboration.isFetching} />
        {submitProposalDialog}
      </>
    );
  }

  const drawersWidth = getProposalDrawerWidth({
    teamSelectionOpen: isTeamSelectionDrawerOpen,
    commentsOpen: commentsDrawerIsOpen,
    addFeeOpen: isAddFeePanelOpen,
    existingFeeOpen: isExistingFeePanelOpen,
    canSeatSidePanel,
  });

  const networkRequestIsInFlight =
    isLoading ||
    isSaving ||
    isSubmitting ||
    giveReviewerApprovalResult.isLoading ||
    completeProposalReviewResult.isLoading;
  const proposalEditingIsPaused = networkRequestIsInFlight || proposalControlsArePaused;
  const proposalEditingIsDisabled = Boolean(
    proposalEditingIsPaused ||
    isReadOnlyView ||
    reviewCompletionLocksEditing ||
    (proposal && !canEditProposal),
  );
  // The same locks, named for the field history that cannot restore a value while one holds (#6874).
  const historyReadOnlyReason = proposalFieldRestoreLockReason({
    cannotEdit: Boolean(proposal && !canEditProposal),
    reviewCompleted: Boolean(reviewCompletionLocksEditing),
    isViewOnly: isReadOnlyView,
    isPaused: proposalEditingIsPaused,
  });

  // A firm revising a proposal sent to several firms is offered its team's published rates while the
  // fee schedule has none, as the vendor's own draft is (#6468). The rates as they stand are the
  // revision's current entries, not the proposal's last sent schedule.
  const offeredFeeScheduleRedline = proposal?.redline?.feeSchedule;
  const publishedRatesOfferTeam = publishedRatesOfferTeamId({
    viewingAsVendor: session.context?.viewingAsVendor,
    vendorTeam: proposal?.vendorTeam,
    feeSchedule: Masked.isPresentNotMasked(offeredFeeScheduleRedline)
      ? offeredFeeScheduleRedline.currentEntries
      : (offeredFeeScheduleRedline ?? proposal?.workAgreement?.feeSchedule),
    mayEdit: !proposalEditingIsDisabled,
    isDismissed: publishedRatesOfferDismissed,
    isOnFeeScheduleTab: activeTab === PageTab.FeeSchedule,
  });

  // #region Secondary Actions
  /**
   * The actions that manage the negotiation rather than advance it, in the order they are offered.
   *
   * They are described here and drawn by `ProposalSecondaryActions`, which shows them as buttons while
   * the pane can seat them and collapses them into one menu once it cannot. Before that, all nine of
   * this page's actions competed for the same wrapping grid in the order they happened to be written,
   * so a 688px pane put Reviewers and Cancel Proposal on the lines above Hire (#5239).
   *
   * The test for membership is whether the action advances the negotiation or manages it. Submitting,
   * approving, hiring and deciding on incoming changes are why the page is open; adding a reviewer,
   * undoing an edit and ending the negotiation can all wait for a click.
   *
   * Cancel Proposal and End Negotiation are last on purpose, and `quiet` is why. Both are drawn
   * pinned to the row's last column (#5240), which makes them definitely positioned, and grid's
   * auto-placement cursor moves forward only: an item that claims a column jumps the cursor to it,
   * so anything listed after one would start from that column and wrap to a row of its own rather
   * than fill the empty tracks to its left. The order here is the order in the row.
   *
   * One literal with conditional spreads rather than a `push` per action: two of these handlers guard
   * themselves with `proposalLifecycleActionInFlightRef`, and handing such a function to any call
   * during render is what `react-hooks/refs` warns about. Nothing here calls anything.
   */
  // An unsent revision of a hired work agreement, when the server lets this reader delete it (#7088).
  const discardableRevision: DiscardableRevision | undefined =
    proposal?.status === ProposalStatus.AwaitingSubmission &&
    proposal.id &&
    proposal.replacesWorkAgreementId &&
    proposal.isDeletableBy(session.user?.id)
      ? { id: proposal.id, workAgreementId: proposal.replacesWorkAgreementId }
      : undefined;
  const secondaryActions: ProposalSecondaryAction[] = [
    ...(!isReviewing && !isReadOnlyView && canManageReviewers
      ? [
          {
            key: "reviewers",
            label: "Reviewers",
            icon: <GroupsIcon />,
            tooltip: totalReviewers === 0 ? "Add/remove proposal reviewers" : undefined,
            badge:
              totalReviewers === 0 ? undefined : (
                <ReviewerApprovalBadge pendingReviewCount={pendingReviewCount} />
              ),
            disabled: isLoading || isSaving || proposalControlsArePaused || !proposal?.id,
            onClick: handleManageReviewers,
          },
        ]
      : []),
    ...(!isReadOnlyView && proposal?.redline?.canBeUndone
      ? [
          {
            key: "undo-all-changes",
            label: "Undo All Changes",
            icon: <UndoIcon />,
            color: "secondary" as const,
            disabled:
              isSubmitting ||
              isLoading ||
              isSaving ||
              proposalControlsArePaused ||
              reviewCompletionLocksEditing ||
              !canEditProposal,
            onClick: handleUndoChanges,
          },
        ]
      : []),
    // An unsent revision offers its owner Delete rather than Cancel, so this stands where Cancel
    // Proposal would (#7088).
    ...(!isReadOnlyView && !isReviewing && discardableRevision
      ? [
          {
            key: "discard-revision",
            label: "Discard Revision",
            icon: <DeleteIcon />,
            destructive: true,
            quiet: true,
            tooltip: "Delete this unsent revision and return to the work agreement",
            loading: isDiscardingRevision,
            disabled: isDiscardingRevision || isSubmitting || isLoading || isSaving || proposalControlsArePaused,
            onClick: () => handleDiscardRevision(discardableRevision),
          },
        ]
      : []),
    ...(!isReadOnlyView &&
    !isReviewing &&
    canCancelNegotiation &&
    proposal?.creator?.userId.isEqualTo(session.user?.id)
      ? [
          {
            key: "cancel-proposal",
            label: "Cancel Proposal",
            icon: <CloseIcon />,
            destructive: true,
            quiet: true,
            tooltip: "Cancel this proposal and end negotiations",
            disabled: isSubmitting || isLoading || isSaving || proposalControlsArePaused || !proposal?.id,
            onClick: handleCancel,
          },
        ]
      : []),
    ...(!isReadOnlyView &&
    !isReviewing &&
    canRejectNegotiation &&
    !proposal?.creator?.userId.isEqualTo(session.user?.id)
      ? [
          {
            key: "end-negotiation",
            label: "End Negotiation",
            icon: <BlockIcon />,
            destructive: true,
            quiet: true,
            disabled: isSubmitting || isLoading || isSaving || proposalControlsArePaused || !proposal?.id,
            onClick: handleEndNegotiation,
          },
        ]
      : []),
  ];

  return (
    <WorkPaneWidthContext.Provider value={paneWidth}>
    <ThemeProvider theme={paneTheme}>
    <PageContainer>
        <ProposalPageContent>
        <Main
          ref={paneRef}
          dimmed={isAddFeePanelOpen || isExistingFeePanelOpen || isTeamSelectionDrawerOpen}
          mobilePadding="compact"
          open={
            commentsDrawerIsOpen ||
            isAddFeePanelOpen ||
            isExistingFeePanelOpen ||
            isTeamSelectionDrawerOpen
          }
        >
          {
            // #region Header
          }
          <StickyPageHeader>
            <HeaderContainer>
              <TitleContainer>
                <TutorialTarget id="proposal-status">
                  <PageTitle variant="h4" aria-label={breadcrumbHeadingName(["Proposals"], WorkVersion.nameWithVersion(pageTitle, WorkVersion.of(proposal?.workAgreement)))}>
                  <PageTitleBreadcrumb label="Proposals" to="/proposals" />
                  {pageTitle}
                  <WorkVersionChip version={WorkVersion.of(proposal?.workAgreement)} />
                  </PageTitle>
                </TutorialTarget>
              </TitleContainer>
              <ActionButtonsContainer>
                <TutorialTarget id="proposal-redlines">
                  <IssueTrackerHeader>
                    <IssueTraverser
                      disabled={disableIssueTraverser}
                      rowOrder={feeScheduleTraversalRowOrder}
                      issueFields={uniqBy(issues?.entries.map((issue) => issue.field) ?? [], (field) => field.toJSON())}
                      unreadChangeFields={unreadChangeFields}
                      currentField={activeReviewField}
                      lastStop={traversalStop}
                      unreadCommentCounts={unreadCommentCounts}
                      onTraverseToNewField={handleTraverseFromTraverser}
                      onTraverseToUnreadComments={commentProposal ? handleTraverseToUnreadComments : undefined}
                    />
                  </IssueTrackerHeader>
                </TutorialTarget>
                {
                  // #region Save Button
                }
                <ProposalSaveSplitButton
                  compact={paneIsNarrow}
                  disabled={disableHeaderSave}
                  loading={isSaving}
                  tooltip={saveButtonTooltip}
                  options={saveAsProposalTemplate.actions.map((action) => ({
                    key: action.key,
                    label: action.label,
                    icon: action.icon,
                    disabled: action.disabled || isLoading || isSaving || proposalControlsArePaused,
                    onClick: saveAsProposalTemplate.open,
                  }))}
                  onSave={() =>
                    handleSaveChanges({
                      confirmSave: true,
                      confirmationMode: "save",
                    })
                  }
                />
                {
                  // #region Comments Button
                }
                <TutorialTarget id="proposal-comments">
                <CommentButton>
                  <Tooltip enterDelay={600} placement="bottom-end" title="">
                    <ButtonContainer>
                      <HighlightCommentButton
                        // The button holds an icon, and the visible "Comments" label is its
                        // sibling, not its content — and the Tooltip above names the wrapper, never
                        // the button. Without this the control reaches a screen reader unnamed.
                        aria-label={proposalCommentsTitle}
                        active={activeReviewField?.isEqualTo(ProposalField.General)}
                        size="medium"
                        color="primary"
                        disabled={proposalControlsArePaused}
                        onClick={() => {
                          handleOpenCommentsDrawer(ProposalField.General);
                        }}
                      >
                        <Badge
                          variant={unreadCommentCount ? "standard" : "dot"}
                          color="secondary"
                          overlap="circular"
                          badgeContent={unreadCommentCount > 0 ? unreadCommentCount : undefined}
                          invisible={unreadCommentCount === 0}
                        >
                          <CommentIcon fontSize="medium" />
                        </Badge>
                      </HighlightCommentButton>
                      <CommentButtonLabel variant="button" color="primary" disabled={proposalControlsArePaused}>
                        {proposalCommentsTitle}
                      </CommentButtonLabel>
                    </ButtonContainer>
                  </Tooltip>
                </CommentButton>
                </TutorialTarget>
                <CommentButton>
                  <ButtonContainer>
                    <HighlightCommentButton
                      // The visible caption is a sibling of the button, not its content.
                      aria-label={proposalActivityTitle}
                      aria-expanded={Boolean(activityAnchor)}
                      active={Boolean(activityAnchor)}
                      size="medium"
                      color="primary"
                      disabled={!collaboration.envelope?.negotiationId}
                      onClick={(event) => setActivityAnchor(activityAnchor ? null : event.currentTarget)}
                    >
                      <HistoryIcon fontSize="medium" />
                    </HighlightCommentButton>
                    <CommentButtonLabel
                      variant="button"
                      color="primary"
                      disabled={!collaboration.envelope?.negotiationId}
                    >
                      {proposalActivityTitle}
                    </CommentButtonLabel>
                  </ButtonContainer>
                </CommentButton>
                {/* Who holds a grant on the proposal, for an administrator with Member Permissions (#6172). */}
                {proposalAccess.mayOpen && proposal?.id && (
                  <RecordAccessButton onClick={() => { if (proposal?.id) proposalAccess.open(proposal.id); }} />
                )}
                <ProposalActivityPopover
                  anchorEl={activityAnchor}
                  onClose={() => setActivityAnchor(null)}
                  negotiationId={collaboration.envelope?.negotiationId}
                  viewerScope={collaboration.viewerScope}
                  coverage={activityCoverage}
                  carriedOver={carriedOverFieldHistory}
                />
              </ActionButtonsContainer>
            </HeaderContainer>
            {/* A handover waiting on an answer reports a state, so it sits above the actions rather
                than among them -- and inside the header, where it shares the title's edge and leaves
                nothing between the tab band's rule and the tab (#6099). */}
            {leaderHandover.notices && <PageNotices>{leaderHandover.notices}</PageNotices>}
            {leaderHandover.dialog}
            <PageActionsContainer>
              <ProposalActions>
                {
                  // #region Submit Review Button
                }
                {isReviewing && (
                  <Tooltip enterDelay={600} placement="bottom" title={submitReviewTooltip}>
                    <span>
                      <SubmitReviewButton
                        disabled={networkRequestIsInFlight || proposalControlsArePaused}
                        size={paneIsNarrow ? "small" : "medium"}
                        finalized={hasUserApprovedReview}
                        loading={
                          isSaving || giveReviewerApprovalResult.isLoading || completeProposalReviewResult.isLoading
                        }
                        canEdit={canEditProposal}
                        hasPendingChanges={hasPendingReviewerChanges}
                        pendingChangeSummary={{
                          heading: proposalSaveChangeLogHeading({
                            audienceLabel: reviewCompletionSharePlan.audienceLabel,
                          }),
                          descriptors: reviewCompletionSharePlan.descriptors,
                        }}
                        onFinalize={handleFinalizeReviewClicked}
                        onSubmitAllAndFinalize={handleSubmitAllAndFinalizeReviewClicked}
                      />
                    </span>
                  </Tooltip>
                )}
                {isReviewing && <ReviewerLeaderMessage message={currentUserReviewer?.customMessage} />}
                {!isReadOnlyView &&
                  proposal &&
                  // Bulk accept/reject is offered while the current submission still holds counterparty
                  // changes this viewer has not decided. Who originally created the proposal says nothing
                  // about who authored the submission being reviewed — a client who opened the negotiation
                  // still has to resolve the vendor's revisions — and `hasRejections`/`hasRevisions`
                  // describe decisions the viewer has already made, so every one of those gates hid the
                  // buttons from exactly the people sitting on unresolved changes.
                  canEditProposal &&
                  hasUnresolvedIncomingChanges && (
                    <>
                      {
                        // #region Accept / Reject Split Button
                      }
                      <BulkChangeDecisionButton
                        // The pane, not the window: this page is drawn beside a nav and a comments
                        // drawer, which take width no media query can see.
                        size={paneIsNarrow ? "small" : "medium"}
                        acceptDisabled={isSubmitting || isLoading || isSaving || proposalControlsArePaused}
                        rejectDisabled={
                          isSubmitting ||
                          isLoading ||
                          isSaving ||
                          proposalControlsArePaused ||
                          !proposal?.id ||
                          !canEditProposal
                        }
                        onAcceptAll={handleAccept}
                        onRejectAll={handleReject}
                      />
                    </>
                  )}
                {canSubmitApproval && (
                  <Tooltip enterDelay={600} placement="bottom" title={approvalActionTooltip}>
                    <span>
                      <TutorialTarget id="proposal-approval">
                      <ProposalActionButton
                        variant={primaryAction === "approve" ? "contained" : "outlined"}
                        color="success"
                        size={paneIsNarrow ? "small" : "medium"}
                        startIcon={canApproveAndHire ? <WorkIcon /> : <SendIcon />}
                        loading={isSubmitting}
                        disabled={
                          isSubmitting ||
                          isLoading ||
                          isSaving ||
                          proposalControlsArePaused ||
                          !proposal?.redline?.isResolved ||
                          (!session.user?.canApprove(proposal) && isReviewing) ||
                          approvalIsBlockedByIssues
                        }
                        onClick={handleApproveProposal}
                      >
                        {approvalActionLabel}
                      </ProposalActionButton>
                      </TutorialTarget>
                    </span>
                  </Tooltip>
                )}

                {showsSubmitChanges && (
                    <Tooltip enterDelay={600} placement="bottom" title={submitChangesTooltip}>
                      <span>
                        <TutorialTarget id="proposal-submit-changes" enabled={!submitChangesDisabled}>
                        <ProposalActionButton
                          variant={primaryAction === "submitChanges" ? "contained" : "outlined"}
                          color="success"
                          size={paneIsNarrow ? "small" : "medium"}
                          startIcon={<SendIcon />}
                          loading={isSubmitting}
                          disabled={submitChangesDisabled}
                          onClick={handleSubmitRevision}
                        >
                          Submit Changes
                        </ProposalActionButton>
                        </TutorialTarget>
                      </span>
                    </Tooltip>
                  )}

                {
                  // #region Hire Button
                }
                {showsHire && (
                    <Tooltip
                      enterDelay={600}
                      placement="bottom"
                      title={!canHireProposal && "Awaiting approval by vendors"}
                    >
                      <span>
                        <TutorialTarget id="proposal-hire" enabled={!hireProposalDisabled}>
                        <ProposalActionButton
                          variant={primaryAction === "hire" ? "contained" : "outlined"}
                          color="success"
                          size={paneIsNarrow ? "small" : "medium"}
                          startIcon={<WorkIcon />}
                          loading={false}
                          disabled={hireProposalDisabled}
                          onClick={handleHire}
                        >
                          Hire
                        </ProposalActionButton>
                        </TutorialTarget>
                      </span>
                    </Tooltip>
                  )}
                {
                  // #region Secondary Actions
                }
                <ProposalSecondaryActions
                  actions={secondaryActions}
                  collapsed={paneIsNarrow}
                  size={paneIsNarrow ? "small" : "medium"}
                />
                {saveAsProposalTemplate.dialog}
              </ProposalActions>
              {proposalAccess.drawer}
              {/* A popover, so it is not a cell of the actions grid; it hangs off whichever control
                  opened it — the Reviewers button, or "More Actions" once they have collapsed. */}
              {!isReviewing && !isReadOnlyView && canManageReviewers && proposal && (
                <ReviewerSelector
                  proposal={proposal}
                  popoverAnchor={reviewerSelectorAnchor}
                  reviewerSource="proposal"
                  reviewerBaseline={originalReviewers}
                  canRequestReReview={reReview.canRequestReReview}
                  reReviewPendingUserIds={reReview.pendingUserIds}
                  onRequestReReview={reReview.requestReReview}
                  // Only when somebody is pending: the popover insets whatever it is given (review of #6781).
                  pendingReviewerReminders={
                    collaboration.mode === "canonical" &&
                    collaboration.envelope &&
                    pendingProposalReviewers(collaboration.envelope, collaboration.pendingChanges.reviewerChanges)
                      .length > 0 ? (
                      <PendingReviewerReminders
                        envelope={collaboration.envelope}
                        viewerScope={collaboration.viewerScope}
                        stagedReviewerChanges={collaboration.pendingChanges.reviewerChanges}
                      />
                    ) : undefined
                  }
                  proposalHasUnsavedChanges={isDirty}
                  isSendingToReviewers={isSaving}
                  // #4842: the header Save is disabled while the popover is open, so the
                  // popover carries its own way to persist the roster; it closes first so
                  // the confirmation is not stacked behind it. #5134: Send is no longer
                  // gated on this page being dirty, so it asks first only when it is —
                  // when it would flush proposal edits the actor did not press Save for.
                  onSendToReviewers={({ confirmSave }) => {
                    dispatchPanelState({ type: "closeReviewerSelector" });
                    void handleSaveChanges({ confirmSave, confirmationMode: "save" });
                  }}
                  onPopoverClose={() => {
                    dispatchPanelState({
                      type: "closeReviewerSelector",
                    });
                  }}
                  onReviewersChanged={(updatedReviewers) => {
                    const proposalWithUpdatedReviewers = updateCurrentProposalReviewers(updatedReviewers);
                    if (!proposalWithUpdatedReviewers) return;

                    applyPendingUnsavedProposalUpdate(proposalWithUpdatedReviewers);
                  }}
                />
              )}
            </PageActionsContainer>
            <ProposalTabsBand>
              {
                // #region Tabs
              }
              <TabsContainer
                variant="scrollable"
                scrollButtons="auto"
                indicatorColor="primary"
                textColor="primary"
                allowScrollButtonsMobile
                value={activeTab}
                onChange={handleTabChange}
              >
                <ProposalTab
                  value={PageTab.Details}
                  disabled={shouldDisableTabs}
                  label={
                    <TutorialTarget id="proposal-terms">
                      <ProposalIssuesBadge
                        issues={issues}
                        category={ProposalFieldCategory.Details}
                        envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                        currentUserId={session.user?.id?.value}
                        onIssueSelected={handleIssueSelected}
                        {...tabPendingCounts.forCategory(ProposalFieldCategory.Details)}
                      />
                    </TutorialTarget>
                  }
                />
                <ProposalTab
                  value={PageTab.ClientTeam}
                  disabled={shouldDisableTabs}
                  label="Client Team"
                />
                <ProposalTab
                  value={PageTab.VendorTeam}
                  disabled={shouldDisableTabs}
                  label={
                    <ProposalIssuesBadge
                      title="Vendor Team"
                      issues={issues}
                      category={ProposalFieldCategory.Team}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      onIssueSelected={handleIssueSelected}
                      {...tabPendingCounts.forCategory(ProposalFieldCategory.Team)}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.FeeSchedule}
                  disabled={shouldDisableTabs}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      category={ProposalFieldCategory.FeeSchedule}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      onIssueSelected={handleIssueSelected}
                      {...tabPendingCounts.forCategory(ProposalFieldCategory.FeeSchedule)}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.Conflicts}
                  disabled={shouldDisableTabs}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      category={ProposalFieldCategory.Conflicts}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      onIssueSelected={handleIssueSelected}
                      {...tabPendingCounts.forCategory(ProposalFieldCategory.Conflicts)}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.Policies}
                  disabled={shouldDisableTabs}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      category={ProposalFieldCategory.Policies}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      onIssueSelected={handleIssueSelected}
                      {...tabPendingCounts.forCategory(ProposalFieldCategory.Policies)}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.Discount}
                  disabled={shouldDisableTabs}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      category={ProposalFieldCategory.Discount}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      onIssueSelected={handleIssueSelected}
                      {...tabPendingCounts.forCategory(ProposalFieldCategory.Discount)}
                    />
                  }
                />
              </TabsContainer>
            </ProposalTabsBand>
          </StickyPageHeader>
          {
            // #region Page Content
          }
          <Content {...paneFlushContent}>
          {/* Lets the change traverser open a field's history where the field lives — inside a tab — and
              gives each field's history the negotiation a revision continues. */}
          <ProposalFieldHistoryRequestContext.Provider value={fieldHistoryRequest}>
          <ProposalCarriedOverNegotiationContext.Provider value={carriedOverFieldHistory}>
            <TraverserStopScopeContext.Provider value={traverserStopScope}>
            <TabContent ref={attachTraverserStopScope} disableBottomPadding={activeTab === PageTab.FeeSchedule}>
              {(isLoading || !proposal?.redline || !proposal.clientTeam) && (
                <LoaderContainer>
                  <Loader />
                </LoaderContainer>
              )}
              {!isLoading && proposal?.redline && proposal.clientTeam && (
                <>
                    {activeTab === PageTab.Details && (
                      <DetailsTab
                        proposal={proposal}
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalStatus={proposal.displayStatus}
                        proposalRedline={proposal.redline}
                        persistedRedline={persistedRedline}
                        isReviewing={isReviewing}
                        issues={issues}
                        disableEditing={proposalEditingIsDisabled}
                        historyReadOnlyReason={historyReadOnlyReason}
                        activeReviewField={activeReviewField}
                        onProposalRedlineChange={handleProposalRedlineChanged}
                        onTraverseToNewField={handleTraverseToNewField}
                        clientLeaderUserId={
                          Masked.isPresentNotMasked(proposal.clientLeader) ? proposal.clientLeader.userId : null
                        }
                        clientRepresentativeName={collaboration.envelope?.clientRepresentativeName}
                      />
                    )}
                    {activeTab === PageTab.ClientTeam && (
                      <ClientTeamTab
                        proposal={proposal}
                        leaderActions={leaderHandover.clientLeaderActions}
                        negotiationId={collaboration.envelope?.negotiationId}
                        canEdit={collaboration.envelope?.capabilities.canManageClientTeam}
                      />
                    )}
                    {activeTab === PageTab.VendorTeam && (
                      <TeamTab
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalRedline={proposal.redline}
                        issues={issues}
                        isDrawerOpen={commentsDrawerIsOpen || isTeamSelectionDrawerOpen}
                        disableEditing={proposalEditingIsDisabled}
                        activeReviewField={activeReviewField}
                        teamLeader={
                          MaskedTeam.isPresentNotMasked(proposal.vendorTeam) ? proposal.vendorTeam.leader : undefined
                        }
                        leaderActions={leaderHandover.vendorLeaderActions}
                        onProposalRedlineChange={handleProposalRedlineChanged}
                        onTraverseToNewField={handleTraverseToNewField}
                        onTeamMemberQuitProposal={handleTeamMemberQuitProposal}
                        onSelectionDrawerOpenChange={handleTeamSelectionDrawerOpenChange}
                        onTeamLeaderReverted={handleTeamLeaderReverted}
                        selectedTemplateIds={proposal.currentPartyTeamTemplateIds}
                        onTeamTemplatesUpdated={(templateIds, newRedline) =>
                          handleTeamTemplatesUpdated(proposal, templateIds, newRedline)
                        }
                      />
                    )}
                    {publishedRatesOfferTeam && (
                      <PublishedRatesOffer
                        marketplaceTeamId={publishedRatesOfferTeam}
                        onApply={(schedule) => void handleExistingCategoriesSelected(schedule.copyCategoriesForProposal())}
                        onDismiss={() => setPublishedRatesOfferDismissed(true)}
                      />
                    )}
                    {activeTab === PageTab.FeeSchedule && (
                      <FeeScheduleTab
                        onDisplayedCategoriesChange={setDisplayedFeeScheduleCategoryIds}
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalRedline={proposal.redline}
                        issues={issues}
                        disableEditing={proposalEditingIsDisabled}
                        isDrawerOpen={isAddFeePanelOpen || isExistingFeePanelOpen || commentsDrawerIsOpen}
                        activeReviewField={activeReviewField}
                        onProposalRedlineChange={handleProposalRedlineChanged}
                        onCreateCategoryClicked={handleCreateCategory}
                        onEditCategoryClicked={handleEditCategory}
                        onIssueSelected={handleIssueSelected}
                        onExistingCategoryClicked={handleAddExistingCategory}
                        onTraverseToNewField={handleTraverseToNewField}
                        teamMembers={getFeeScheduleTeamMembers(proposal?.vendorTeam)}
                        teamRoster={getFeeScheduleTeamRosterFromRedline(proposal?.vendorTeam, proposal?.redline, proposal?.clientTeam)}
                        teamMemberDepartures={replacedWorkDepartures}
                        selectedTemplateIds={proposal.currentPartyFeeScheduleTemplateIds}
                        onFeeScheduleTemplatesUpdated={(templateIds, newRedline) =>
                          handleFeeScheduleTemplatesUpdated(proposal, templateIds, newRedline)
                        }
                      />
                    )}
                    {activeTab === PageTab.Conflicts && (
                      <ConflictsTab
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalId={proposal.id}
                        proposalRedline={proposal.redline}
                        issues={issues}
                        isDrawerOpen={commentsDrawerIsOpen}
                        disableEditing={proposalEditingIsDisabled}
                        activeReviewField={activeReviewField}
                        onProposalRedlineChange={handleProposalRedlineChanged}
                        onTraverseToNewField={handleTraverseToNewField}
                      />
                    )}
                    {activeTab === PageTab.Policies && (
                      <PoliciesTab
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalId={proposal.id}
                        proposalRedline={proposal.redline}
                        issues={issues}
                        unreadEnvelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                        currentUserId={session.user?.id?.value}
                        isDrawerOpen={commentsDrawerIsOpen}
                        disableEditing={proposalEditingIsDisabled}
                        activeReviewField={activeReviewField}
                        onProposalRedlineChange={handleProposalRedlineChanged}
                        onTraverseToNewField={handleTraverseToNewField}
                      />
                    )}
                    {activeTab === PageTab.Discount && (
                      <DiscountTab
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalRedline={proposal.redline}
                        issues={issues}
                        disableEditing={proposalEditingIsDisabled}
                        activeReviewField={activeReviewField}
                        onProposalRedlineChange={handleProposalRedlineChanged}
                        onTraverseToNewField={handleTraverseToNewField}
                      />
                    )}
                </>
              )}
            </TabContent>
            </TraverserStopScopeContext.Provider>
          </ProposalCarriedOverNegotiationContext.Provider>
          </ProposalFieldHistoryRequestContext.Provider>
          </Content>
          <TutorialTarget id="proposal-timeline">
          <IssueTrackerFooter>
            <IssueTraverser
              disabled={disableIssueTraverser}
              rowOrder={feeScheduleTraversalRowOrder}
              issueFields={uniqBy(issues?.entries.map((issue) => issue.field) ?? [], (field) => field.toJSON())}
              // The header traverser has always stopped at fields carrying an unviewed change; the
              // footer one silently did not, so the same button skipped them depending on where the
              // user clicked it.
              unreadChangeFields={unreadChangeFields}
              currentField={activeReviewField}
              lastStop={traversalStop}
              unreadCommentCounts={unreadCommentCounts}
              onTraverseToNewField={handleTraverseFromTraverser}
              onTraverseToUnreadComments={commentProposal ? handleTraverseToUnreadComments : undefined}
            />
          </IssueTrackerFooter>
          </TutorialTarget>
        </Main>
        </ProposalPageContent>
        {
          // #region Drawers
        }
        <SidePanelColumn
          style={{
            width: drawersWidth,
          }}
        >
          {commentProposal && (
            <CommentsDrawer
              proposal={commentProposal}
              commentThreads={commentThreads}
              isOpen={commentsDrawerIsOpen}
              isSaving={isSaving}
              title={commentsDrawerTitle}
              carriedOverNegotiationId={carriedOverNegotiationId}
              carriedOverRefused={carriedOverRefused}
              commentToScrollTo={commentToScrollTo}
              conversationToOpen={traversalStop?.kind === "unreadComments" ? traversalStop : undefined}
              forumRequest={commentsForumRequest}
              onOpen={handleOpenCommentsDrawer}
              onClose={handleCloseCommentsDrawer}
            />
          )}
          <EditFeeScheduleCategoryDrawer
            key={currentCategory?.id.value ?? newFeeCategoryFormKey}
            category={currentCategory}
            formKey={newFeeCategoryFormKey}
            isOpen={isAddFeePanelOpen}
            onOpen={() =>
              dispatchPanelState({
                type: "openAddFeePanel",
                preserveCategory: true,
              })
            }
            onClose={() => {
              dispatchPanelState({
                type: "closeAddFeePanel",
                clearCategory: true,
              });
            }}
            onCategoryAdded={handleCategoryAdded}
            onCategoryUpdated={(_originalName, savedCategory) => handleCategoryUpdated(savedCategory)}
            selectedCategories={selectedFeeScheduleCategories}
            offersTeamMember
            focus={panelState.categoryEditorFocus}
            teamRoster={getFeeScheduleTeamRosterFromRedline(proposal?.vendorTeam, proposal?.redline, proposal?.clientTeam)}
            redline={currentCategoryRedline && {
              categoryRedline: currentCategoryRedline,
              negotiationId: collaboration.envelope?.negotiationId,
              readOnly: proposalEditingIsDisabled,
            }}
          />
          <ExistingFeeScheduleCategoryDrawer
            selectedCategories={proposal?.workAgreement?.feeSchedule ?? []}
            isOpen={isExistingFeePanelOpen}
            onOpen={() => dispatchPanelState({ type: "openExistingFeePanel" })}
            onClose={() => dispatchPanelState({ type: "closeExistingFeePanel" })}
            onCategoriesSelected={handleExistingCategoriesSelected}
            proposalId={negotiationGuid?.toString()}
            // What the vendor team advertises, offered as a second source (#5113). Undefined when
            // the team did not come from the marketplace, or when the team is masked to this viewer.
            marketplaceTeamId={marketplaceVendorTeamId}
          />
          {submitProposalDialog}
        </SidePanelColumn>
      </PageContainer>
    </ThemeProvider>
    </WorkPaneWidthContext.Provider>
  );
  // #endregion
}

// #region Helper Functions
function getProposalSavePatchPayload(args: {
  originalProposal?: Proposal | null;
  updatedProposal?: Proposal | null;
  session: Session;
}): JsonPatchOperation[] | null {
  const { originalProposal, updatedProposal, session } = args;
  if (!(originalProposal instanceof Proposal) || !(updatedProposal instanceof Proposal)) return null;

  try {
    return new UpdateProposalAPIRequest({
      originalProposal,
      proposal: updatedProposal,
      session,
    }).payload;
  } catch {
    return null;
  }
}

function getReviewerUpdateSourceProposal(args: {
  fallbackProposal: Proposal | null | undefined;
  proposal: Proposal | null | undefined;
  viewingAsVendor: boolean;
}): Proposal | null {
  if (proposalHasReviewerUpdateMethod(args.proposal, args.viewingAsVendor)) return args.proposal;
  if (proposalHasReviewerUpdateMethod(args.fallbackProposal, args.viewingAsVendor)) return args.fallbackProposal;
  return null;
}

function proposalHasReviewerUpdateMethod(
  proposal: Proposal | null | undefined,
  viewingAsVendor: boolean,
): proposal is Proposal {
  if (!proposal) return false;

  return viewingAsVendor
    ? typeof proposal.updateVendorReviewers === "function"
    : typeof proposal.updateClientReviewers === "function";
}

function getProposalJSONForServerCache(proposal: Proposal, fallbackJSON: ProposalJSON): ProposalJSON {
  const proposalWithOptionalJSON = proposal as Proposal & {
    toJSON?: () => ProposalJSON;
  };
  if (typeof proposalWithOptionalJSON.toJSON === "function") return proposalWithOptionalJSON.toJSON();
  return fallbackJSON;
}

function proposalFieldCategoryFromPageTab(pageTab: PageTab): ProposalFieldCategoryType {
  switch (pageTab) {
    case PageTab.Details:
      return ProposalFieldCategory.Details;
    case PageTab.VendorTeam:
      return ProposalFieldCategory.Team;
    case PageTab.ClientTeam:
      return ProposalFieldCategory.Details;
    case PageTab.FeeSchedule:
      return ProposalFieldCategory.FeeSchedule;
    case PageTab.Conflicts:
      return ProposalFieldCategory.Conflicts;
    case PageTab.Policies:
      return ProposalFieldCategory.Policies;
    case PageTab.Discount:
      return ProposalFieldCategory.Discount;
  }
}

function pageTabFromProposalFieldCategory(proposalFieldCategory: ProposalFieldCategoryType): PageTab {
  switch (proposalFieldCategory) {
    case ProposalFieldCategory.Details:
      return PageTab.Details;
    case ProposalFieldCategory.Team:
      return PageTab.VendorTeam;
    case ProposalFieldCategory.FeeSchedule:
      return PageTab.FeeSchedule;
    case ProposalFieldCategory.Conflicts:
      return PageTab.Conflicts;
    case ProposalFieldCategory.Policies:
      return PageTab.Policies;
    case ProposalFieldCategory.Discount:
      return PageTab.Discount;
  }
}
